// src/views/trading/MarketWatch.js
import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import "../../assets/scss/MarketWatch.scss";
import "../../assets/scss/mw-mt5-quotes.scss";
import "../../assets/scss/mw-row-action-sheet.scss";
import "../../assets/scss/mw-spec-sheet.scss";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { getSegmentOptionList, getSymbolList, showMessage } from "Store/Reducer/common.slice";
import {
  deleteMarketWatch,
  multipleDeleteMarketWatch,
  getExpiryDatesBySymbol,
  getIndexScript,
  getMarketWatchList,
  reorderMarketWatchListOrder,
  setMarketWatchList,
  setScriptIndexList,
} from "Store/Reducer/marketwatch.slice";
import { api } from "utils/api";
import { DARK_UI } from "utils/darkUiPalette";
import { resolveFreshWatchRow } from "components/trading/MarketWatchQuickOrderModal";
import { getSocket } from "utils/socketInstance";
import {
  indexRowsSubscriptionTokens,
  emitIndexSocketSubscriptions,
  indexRowIdentityTokens,
  pickReasonablePrevClose,
  canonicalBenchmarkIndexAlias,
  rowBenchmarkIndexAlias,
  namesShareScriptFamily,
} from "utils/indexStripRows";
import { normalizeTickPayload } from "utils/socketTick";
import { getAxiosErrorMessage } from "utils/httpErrors";
import useAppLanguage from "hooks/useAppLanguage";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/** Flatten script/symbol API payloads (get-all-script-by-user, symbol lists, etc.) */
function normalizeMwSymbolArray(payload) {
  if (payload == null) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.records)) return payload.records;
  if (Array.isArray(payload.data)) return payload.data;
  if (payload.data && Array.isArray(payload.data.data)) return payload.data.data;
  return [];
}

function extractMwSymbolLabel(item) {
  if (item == null) return "";
  if (typeof item === "string") return String(item).trim();
  const o = item;
  const s =
    o.symbol ??
    o.Symbol ??
    o.script ??
    o.Script ??
    o.name ??
    o.Name ??
    o.displayName ??
    o.DisplayName ??
    o.label ??
    o.Label ??
    o.value ??
    o.text ??
    "";
  return String(s || "").trim();
}

function collectSymbolNamesMatchingQuery(items, qLower) {
  const names = new Set();
  if (!qLower) return names;
  for (const item of Array.isArray(items) ? items : []) {
    const label = extractMwSymbolLabel(item);
    if (!label) continue;
    if (label.toLowerCase().includes(qLower)) names.add(label);
  }
  return names;
}

function buildScriptSuffix(ddmmyyyy) {
  const parts = ddmmyyyy.split("-");
  if (parts.length !== 3) return ddmmyyyy;
  const [dd, mm, yyyy] = parts;
  return `${dd}${MONTHS[Number(mm) - 1] || ""}${yyyy}`;
}

function buildWatchlistSpecSections(row, t = (k) => k) {
  if (!row || typeof row !== "object") return [];
  const txt = (...keys) => {
    for (const k of keys) {
      const v = row[k];
      if (v != null && String(v).trim() !== "") return String(v).trim();
    }
    return "";
  };
  const num = (...keys) => {
    for (const k of keys) {
      const n = Number(row[k]);
      if (Number.isFinite(n)) return n;
    }
    return null;
  };
  const fmtN = (n, d = 2) => (n == null ? "—" : Number(n).toFixed(d));
  const fmtPct = () => {
    const p = row?.changePercent ?? row?.percentChange;
    if (p == null || p === "") return "—";
    const n = Number(p);
    return Number.isFinite(n) ? `${n.toFixed(2)}%` : String(p);
  };
  const currency = txt("currency", "Currency") || "USD";
  const swapVal = txt("swap", "Swap", "swapRate") || "—";
  const weekdays = [
    ["weekdaySun", t("weekdaySun")],
    ["weekdayMon", t("weekdayMon")],
    ["weekdayTue", t("weekdayTue")],
    ["weekdayWed", t("weekdayWed")],
    ["weekdayThu", t("weekdayThu")],
    ["weekdayFri", t("weekdayFri")],
    ["weekdaySat", t("weekdaySat")],
  ];

  return [
    {
      title: t("margin"),
      rows: [
        { label: t("margin"), value: `${t("margin")} · ${currency}` },
        { label: t("lot"), value: txt("lot", "qty", "netLot") || "—" },
        { label: t("segment"), value: txt("segment", "segmentName") || "—" },
      ],
    },
    {
      title: t("market"),
      rows: [
        { label: t("bid"), value: fmtN(num("buyPrice", "bid")) },
        { label: t("ask"), value: fmtN(num("sellPrice", "ask")) },
        { label: t("ltp"), value: fmtN(num("lastTradedPrice", "ltp")) },
        { label: t("dailyChange"), value: fmtPct() },
        { label: t("open"), value: fmtN(num("open")) },
        { label: t("high"), value: fmtN(num("high")) },
        { label: t("low"), value: fmtN(num("low")) },
        { label: t("prevClose"), value: fmtN(num("close", "previousClose")) },
        { label: t("volume"), value: txt("volume") ? Number(row.volume).toLocaleString() : "—" },
      ],
    },
    {
      title: t("sessions"),
      rows: weekdays.map(([, label]) => ({
        label,
        value: `${t("quotes")}  —    ${t("trade")}  —`,
      })),
    },
    {
      title: t("swapRates"),
      rows: weekdays.slice(1, 6).map(([, label]) => ({
        label,
        value: swapVal,
      })),
    },
  ];
}

/** MT5 Market Watch tick colors: blue = up, red = down */
const MW_MT5_TICK_UP = "#0066b3";
const MW_MT5_TICK_DOWN = "#c41e3a";

/**
 * Use live tick color only (colorBid / colorAsk / colorLTP).
 * If unavailable, keep Bid/Ask/LTP black — no Daily Change fallback.
 */
function mwMt5PriceColor(tickColor, _row, flatFallback = "#1a1a1a") {
  if (tickColor) return tickColor;
  return flatFallback;
}

function mwMt5DirFromRow(row) {
  const bidC = row?.colorBid || "";
  const askC = row?.colorAsk || "";
  const ltpC = row?.colorLTP || "";
  const up = MW_MT5_TICK_UP;
  const down = MW_MT5_TICK_DOWN;
  // Also accept legacy green/red so mid-session ticks still show a direction
  const isUp = (c) => c === up || c === "#009846" || c === "#16a34a";
  const isDown = (c) => c === down || c === "#BF2114" || c === "#dc2626";
  if (isUp(bidC) || isUp(askC) || isUp(ltpC)) return "up";
  if (isDown(bidC) || isDown(askC) || isDown(ltpC)) return "down";
  const chg = Number(row?.changePercent ?? row?.changeAmount);
  if (Number.isFinite(chg) && chg > 0) return "up";
  if (Number.isFinite(chg) && chg < 0) return "down";
  return "flat";
}

function MwMt5DirIcon({ dir }) {
  if (dir === "up") {
    return (
      <span className="mw-mt5-dir mw-mt5-dir--up" aria-hidden title="Up">
        <svg viewBox="0 0 8 8" fill="currentColor">
          <path d="M4 1.2L7.2 6.5H0.8L4 1.2z" />
        </svg>
      </span>
    );
  }
  if (dir === "down") {
    return (
      <span className="mw-mt5-dir mw-mt5-dir--down" aria-hidden title="Down">
        <svg viewBox="0 0 8 8" fill="currentColor">
          <path d="M4 6.8L0.8 1.5h6.4L4 6.8z" />
        </svg>
      </span>
    );
  }
  return (
    <span className="mw-mt5-dir mw-mt5-dir--flat" aria-hidden>
      <svg viewBox="0 0 8 8" fill="currentColor">
        <rect x="1" y="3.2" width="6" height="1.6" rx="0.2" />
      </svg>
    </span>
  );
}

function MwWatchlistRemoveIcon() {
  return (
    <svg className="mw-watchlist-remove-btn__svg" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M8 6h8M10 6V5a1 1 0 011-1h2a1 1 0 011 1v1M7 6l.8 11.2a1.5 1.5 0 001.5 1.3h5.4a1.5 1.5 0 001.5-1.3L17 6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M10 10v5M14 10v5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function MwWatchlistClearAllIcon() {
  return (
    <svg className="mw-watchlist-clear-all-btn__svg" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2M10 11v6M14 11v6"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M7 7l1 12a2 2 0 002 2h4a2 2 0 002-2l1-12" stroke="currentColor" strokeWidth="1.65" strokeLinejoin="round" />
    </svg>
  );
}

function MwWatchlistRemoveButton({ onClick, busy = false }) {
  return (
    <button
      type="button"
      className={`mw-watchlist-remove-btn${busy ? " mw-watchlist-remove-btn--busy" : ""}`}
      title="Remove symbol"
      aria-label="Remove symbol from watchlist"
      onClick={onClick}
      disabled={busy}
    >
      <MwWatchlistRemoveIcon />
    </button>
  );
}

function MwWatchlistClearAllHeaderButton({ onClick, disabled }) {
  return (
    <button
      type="button"
      className="mw-watchlist-clear-all-btn"
      title="Clear entire watchlist"
      aria-label="Clear entire watchlist"
      onClick={onClick}
      disabled={disabled}
    >
      <MwWatchlistClearAllIcon />
    </button>
  );
}

function normalizeDateStr(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  if (/^\d{2}-\d{2}-\d{4}$/.test(s)) return s;                         // DD-MM-YYYY
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {                                  // YYYY-MM-DD
    const [yyyy, mm, dd] = s.split("-");
    return `${dd}-${mm}-${yyyy}`;
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s.replace(/\//g, "-");   // DD/MM/YYYY
  if (/^\d{8}$/.test(s)) {                                               // YYYYMMDD (DB format)
    const yyyy = s.slice(0, 4), mm = s.slice(4, 6), dd = s.slice(6, 8);
    return `${dd}-${mm}-${yyyy}`;
  }
  if (/^\d{4}\/\d{2}\/\d{2}$/.test(s)) {                                // YYYY/MM/DD
    const [yyyy, mm, dd] = s.split("/");
    return `${dd}-${mm}-${yyyy}`;
  }
  return "";
}

function pickExpiry(list) {
  if (!Array.isArray(list) || list.length === 0) return "";
  for (const item of list) {
    const candidates = typeof item === "string"
      ? [item]
      : [
          item?.label,
          item?.name,
          item?.expiry,
          item?.value,
          item?.date,
          item?.ExpiryDate,
          item?.expiryDate,
          item?.ExDate,
          item?.exDate,
        ].filter(Boolean);
    for (const c of candidates) {
      const ds = normalizeDateStr(String(c));
      if (ds) return ds;
    }
  }
  return "";
}

/** Matches legacy script suffix like `28Mar2025` (day + Mon + year). */
function hasExpiryStyleSuffix(s) {
  if (s == null) return false;
  return /\d{1,2}[A-Za-z]{3}\d{4}/.test(String(s));
}

/** Watchlist row label with expiry (parity with old UI: symbol + DDMonYYYY when available). */
function formatWatchlistRowLabel(row) {
  if (!row || typeof row !== "object") return "--";
  const dn = String(row.displayName ?? "").trim();
  const sc = String(row.script ?? "").trim();
  const sym = String(row.symbol ?? row.Symbol ?? "").trim();
  if (hasExpiryStyleSuffix(dn)) return dn;
  if (hasExpiryStyleSuffix(sc)) return sc;
  if (sc && sc !== sym && sc.includes(" ")) return sc;
  if (dn && dn !== sym && (dn.includes(" ") || hasExpiryStyleSuffix(dn))) return dn;
  const expRaw =
    row.expiryDate ??
    row.ExpiryDate ??
    row.exDate ??
    row.ExDate ??
    row.expiry ??
    row.Expiry;
  const nd = normalizeDateStr(String(expRaw || ""));
  if (sym && nd) return `${sym} ${buildScriptSuffix(nd)}`;
  return dn || sc || sym || "--";
}

function mwSortValue(row, id) {
  switch (id) {
    case "symbol":
      return String(formatWatchlistRowLabel(row) || "").toLowerCase();
    case "bid":
      return Number(row?.buyPrice ?? row?.bid ?? 0);
    case "ask":
      return Number(row?.sellPrice ?? row?.ask ?? 0);
    case "ltp":
      return Number(row?.lastTradedPrice ?? row?.ltp ?? 0);
    case "chgPct":
      return Number(row?.changePercent ?? 0);
    case "netChg":
      return Number(row?.changeAmount ?? 0);
    case "open":
      return Number(row?.open ?? 0);
    case "high":
      return Number(row?.high ?? 0);
    case "low":
      return Number(row?.low ?? 0);
    case "prevClose":
      return Number(row?.close ?? row?.previousClose ?? 0);
    case "volume":
      return Number(row?.volume ?? 0);
    default:
      return 0;
  }
}

/** Shorter symbol for narrow mobile watchlist (expiry in title tooltip). */
function formatWatchlistRowLabelShort(row) {
  const full = formatWatchlistRowLabel(row);
  const short = full.replace(/\s+\d{1,2}[A-Za-z]{3}\d{4}\s*$/, "").trim();
  return short || full;
}

function browseRowBaseName(sym) {
  if (sym == null) return "";
  if (typeof sym === "string") return String(sym).trim();
  const o = sym;
  return String(
    o.symbol ??
      o.Symbol ??
      o.value ??
      o.name ??
      o.Name ??
      o.displayName ??
      o.DisplayName ??
      o.label ??
      o.Label ??
      ""
  ).trim();
}

/** Normalize get-expiry (and similar) axios bodies — matches thunk + loose API variants. */
function extractExpiryListFromResponse(res) {
  const body = res?.data;
  if (body == null) return [];
  if (Array.isArray(body)) return body;
  const arr = body.data ?? body.Data ?? body.records;
  if (Array.isArray(arr)) return arr;
  if (Array.isArray(body.data?.data)) return body.data.data;
  return [];
}

/** One-call segment payload → `segmentId|symbol` → DDMonYYYY suffix (settings/symbol-expiry/...). */
function ingestBulkSegmentExpiryRows(segId, rows, cache) {
  if (!Array.isArray(rows) || rows.length === 0) return;
  const sid = String(segId);
  for (const raw of rows) {
    if (raw == null) continue;
    const base = browseRowBaseName(raw);
    if (!base) continue;
    const k = `${sid}|${base}`;
    if (cache[k]) continue;
    const nd =
      pickExpiry([raw]) ||
      (typeof raw === "object"
        ? normalizeDateStr(
            String(
              raw.expiryDate ??
                raw.ExpiryDate ??
                raw.exDate ??
                raw.ExDate ??
                raw.expiry ??
                raw.Expiry ??
                ""
            )
          )
        : "");
    if (nd) cache[k] = buildScriptSuffix(nd);
  }
}

/**
 * Fills missing `segId|symbol` keys in cache using get-expiry-dates-by-symbol.
 * High concurrency + single caller bump — avoids one-row-at-a-time UI updates.
 */
async function fetchExpirySuffixesMissing({ segId, names, cache, concurrency = 28 }) {
  const sid = String(segId);
  const pending = [...new Set(names)].filter((n) => n && !cache[`${sid}|${n}`]);
  if (!pending.length) return;

  let cursor = 0;
  const worker = async () => {
    while (true) {
      const j = cursor++;
      if (j >= pending.length) return;
      const my = pending[j];
      const k = `${sid}|${my}`;
      if (cache[k]) continue;
      try {
        const r = await api.get(
          `/market-watch/get-expiry-dates-by-symbol/${sid}/${encodeURIComponent(my)}`
        );
        const d = extractExpiryListFromResponse(r);
        const ds = pickExpiry(d);
        if (ds) cache[k] = buildScriptSuffix(ds);
      } catch {
        /* ignore */
      }
    }
  };
  const n = Math.min(Math.max(1, concurrency), pending.length);
  await Promise.all(Array.from({ length: n }, () => worker()));
}

/** Add-to-watchlist lists: use row expiry/label from API object first; else cached fetch suffix. */
function browseRowShownLabel(sym, segmentId, suffixMap) {
  const base = browseRowBaseName(sym);
  if (!base) return "—";
  if (typeof sym === "object" && sym) {
    const full = String(
      sym.label ??
        sym.Label ??
        sym.displayName ??
        sym.DisplayName ??
        sym.script ??
        sym.Script ??
        ""
    ).trim();
    if (
      full &&
      full !== base &&
      (full.length > base.length + 2 || hasExpiryStyleSuffix(full) || /\s/.test(full))
    ) {
      return full;
    }
    const expRaw =
      sym.expiryDate ??
      sym.ExpiryDate ??
      sym.exDate ??
      sym.ExDate ??
      sym.expiry ??
      sym.Expiry;
    const nd = normalizeDateStr(String(expRaw || ""));
    if (nd) return `${base} ${buildScriptSuffix(nd)}`;
  }
  const seg = segmentId != null && segmentId !== "" ? String(segmentId) : "";
  const suf = seg ? suffixMap[`${seg}|${base}`] : "";
  return suf ? `${base} ${suf}` : base;
}

function globalHitShownLabel(hit, suffixMap) {
  const n = String(hit?.name ?? "").trim();
  if (!n) return "—";
  const seg = String(hit?.segment?.value ?? "");
  const suf = seg ? suffixMap[`${seg}|${n}`] : "";
  return suf ? `${n} ${suf}` : n;
}

function getSessionData() {
  try {
    const raw = sessionStorage.getItem("TradeSession");
    if (raw) return JSON.parse(window.atob(raw));
  } catch {}
  for (const k of ["fxbazzar_user", "fxbazzar_admin"]) {
    try {
      const raw = sessionStorage.getItem(k);
      if (raw) return JSON.parse(raw);
    } catch {}
  }
  return {};
}

// Persist last-known ticks so refresh (esp. after market close) can show previous values.
const TICK_CACHE_PREFIX = "fxbazzar_tick_cache";
function tickCacheStorageKey(userId) {
  return userId ? `${TICK_CACHE_PREFIX}_${userId}` : `${TICK_CACHE_PREFIX}_anon`;
}
function normalizeTickKey(v) {
  return String(v ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");
}

// ─────────────────────────────────────────────────────────────────────────────
// MarketWatch
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Collapsed “full” watchlist (Symbol+Bid+Ask+LTP+✕): column floors scale with row font (~12px base)
 * so min scroll width tracks text size; add gaps + horizontal padding so ✕ stays inside scroll range.
 */
function mwCollapsedFullScrollMetrics(dragW, delW, wlFontRow, wlColGap, miniWl) {
  const s = (Number(wlFontRow) || 12) / 12;
  const symMin = Math.max(140, Math.round(160 * s));
  const bidMin = Math.max(58, Math.round(72 * s));
  const askMin = Math.max(58, Math.round(72 * s));
  const ltpMin = Math.max(52, Math.round(64 * s));
  const template = `${dragW}px minmax(${symMin}px, 2fr) minmax(${bidMin}px, 1fr) minmax(${askMin}px, 1fr) minmax(${ltpMin}px, 1fr) ${delW}px`;
  const trackMinSum = dragW + symMin + bidMin + askMin + ltpMin + delW;
  const gaps = 5 * wlColGap;
  const padH = miniWl ? 16 : 28;
  return { template, minOuter: trackMinSum + gaps + padH };
}

/** Split "SYMBOL 28Apr2026" into name + expiry for compact mobile rows. */
function splitWatchlistLabel(label) {
  const full = String(label || "").trim();
  const m = full.match(/^(.+?)\s+(\d{1,2}[A-Za-z]{3}\d{4})$/);
  if (m) return { name: m[1].trim(), expiry: m[2] };
  return { name: full || "--", expiry: null };
}

/** Mobile Quotes: symbol only. Expiry is on the row-tap option popup. */
function MobileSymbolLabel({ fullLabel }) {
  const { name } = splitWatchlistLabel(fullLabel);
  return (
    <span className="mw-mt5-symbol-text">
      <span className="mw-symbol-label">
        <span className="mw-symbol-label--name">{name}</span>
      </span>
    </span>
  );
}

/**
 * Mobile fullscreen quotes layout.
 * MT5-style density: Symbol | Bid | Ask | LTP (+ optional cols) — LTP kept; H-scroll OK.
 */
const MW_SYMBOL_FIT_FONT =
  '400 14px "Segoe UI", Tahoma, Geneva, Verdana, sans-serif';

/** Pixel width of the longest watchlist symbol (+ expiry) + dir icon + cell pad. */
function measureWatchlistSymbolColPx(labels, fontPx) {
  const size = Number(fontPx) || 14;
  const list = Array.isArray(labels) ? labels : [];
  let max = 0;
  if (typeof document !== "undefined") {
    if (!measureWatchlistSymbolColPx._ctx) {
      measureWatchlistSymbolColPx._ctx = document.createElement("canvas").getContext("2d");
    }
    const ctx = measureWatchlistSymbolColPx._ctx;
    ctx.font = MW_SYMBOL_FIT_FONT.replace("14px", `${size}px`);
    for (const label of list) {
      const t = String(label || "").trim();
      if (!t) continue;
      const w = ctx.measureText(t).width;
      if (w > max) max = w;
    }
  }
  if (max <= 0) {
    const longest = list.reduce((a, b) => (String(b || "").length > String(a || "").length ? b : a), "");
    max = String(longest || "").length * size * 0.62;
  }
  if (max <= 0) return 72;
  // triangle 9px + 3px gap + row pad ~8px each side
  return Math.ceil(max + 12 + 16 + 2);
}

function mwMobileOptionalSplitMetrics(dragW, delW, wlColGap, optionalCount, wlFontRow, mt5Quotes = false) {
  const s = (Number(wlFontRow) || 12) / 12;
  const symW = Math.max(148, Math.round(168 * s));
  const bidW = Math.max(72, Math.round(78 * s));
  const askW = bidW;
  const optW = Math.max(70, Math.round(78 * s));

  if (mt5Quotes) {
    const bidAskW = Math.max(72, Math.round(78 * s));
    const ltpW = Math.max(72, Math.round(78 * s));
    const optCols =
      optionalCount > 0 ? ` repeat(${optionalCount}, minmax(${optW}px, 1fr))` : "";
    const gaps = 2 * wlColGap;
    return {
      useSplit: true,
      mt5Quotes: true,
      symW: 72,
      tailGridCols: `minmax(${bidAskW}px, 1fr) minmax(${bidAskW}px, 1fr) minmax(${ltpW}px, 1fr)${optCols}`,
      tailMinPx: bidAskW + bidAskW + ltpW + optionalCount * optW + gaps + 8,
      pinnedGridCols: `minmax(0, 1fr)`,
      pinnedWidthPx: 72,
    };
  }

  const ltpW = Math.max(68, Math.round(74 * s));
  const optCols = optionalCount > 0 ? ` repeat(${optionalCount}, ${optW}px)` : "";
  const tailGridCols = `${bidW}px ${askW}px ${ltpW}px${optCols} ${delW}px`;
  const colCount = 3 + optionalCount + 1;
  const tailMinPx = bidW + askW + ltpW + optionalCount * optW + delW + (colCount - 1) * wlColGap + 12;
  return {
    useSplit: true,
    mt5Quotes: false,
    symW,
    tailGridCols,
    tailMinPx,
    pinnedGridCols: `${dragW}px ${symW}px`,
    pinnedWidthPx: dragW + symW + wlColGap,
  };
}

/** Optional subtitle under browse symbol name (existing API fields only). */
function browseRowDescription(sym) {
  if (sym == null || typeof sym === "string") return "";
  return String(
    sym.description ??
      sym.Description ??
      sym.symbolDescription ??
      sym.SymbolDescription ??
      sym.fullName ??
      sym.FullName ??
      ""
  ).trim();
}

function formatHms(d = new Date()) {
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function parseQuoteTime(raw) {
  if (raw == null || raw === "") return "";
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const ms = raw < 1e12 ? raw * 1000 : raw;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? "" : formatHms(d);
  }
  const s = String(raw).trim();
  const m = s.match(/(\d{1,2}:\d{2}(?::\d{2})?)/);
  if (m) {
    const parts = m[1].split(":");
    const hh = String(parts[0]).padStart(2, "0");
    const mm = String(parts[1] || "00").padStart(2, "0");
    const ss = String(parts[2] || "00").padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "" : formatHms(d);
}

function rowQuoteTime(row) {
  return (
    parseQuoteTime(row?.quoteTime) ||
    parseQuoteTime(row?.ltt) ||
    parseQuoteTime(row?.Ltt) ||
    parseQuoteTime(row?.lastTradeTime) ||
    parseQuoteTime(row?.LastTradeTime) ||
    "—"
  );
}

/** MT5-style daily change cell (existing changePercent / color fields). */
function MwMt5DailyChangeCell({ row }) {
  const chgPct = row?.changePercent ?? 0;
  const chgColor = row?.changeAmountColor ?? "";
  const n = Number(chgPct);
  const isUp = chgColor === "text-success" || (Number.isFinite(n) && n > 0);
  const isDown =
    chgColor === "text-danger" ||
    chgColor === "text-error" ||
    (Number.isFinite(n) && n < 0);
  const color = isUp ? "#0066b3" : isDown ? "#c41e3a" : "#666666";
  const text = Number.isFinite(n) ? `${n.toFixed(2)}%` : "0.00%";
  return (
    <div
      className="mw-mt5-daily-change"
      style={{ color, fontWeight: 400, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
    >
      {text}
    </div>
  );
}

const MW_OPTIONAL_WATCH_COLS = [
  { id: "chgPct", label: "Daily Change", title: "Daily Change", desc: "Daily price change as a percentage" },
  { id: "netChg", label: "Net Chg", title: "Net Change", desc: "Absolute change from previous close" },
  { id: "open", label: "Open", title: "Open", desc: "Today’s opening price" },
  { id: "high", label: "High", title: "High", desc: "Day’s highest traded price" },
  { id: "low", label: "Low", title: "Low", desc: "Day’s lowest traded price" },
  { id: "prevClose", label: "Prev. Cl", title: "Prev. Close", desc: "Previous session closing price" },
  { id: "volume", label: "Vol", title: "Volume", desc: "Total quantity traded today" },
];
const MW_OPTIONAL_COL_IDS = MW_OPTIONAL_WATCH_COLS.map((c) => c.id);
const MW_OPTIONAL_COLS_LS_KEY = "fxbazzar_mw_optional_cols";

function loadMwOptionalColIds() {
  try {
    const raw = localStorage.getItem(MW_OPTIONAL_COLS_LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return MW_OPTIONAL_COL_IDS.filter((id) => parsed.includes(id));
  } catch {
    return [];
  }
}

function saveMwOptionalColIds(ids) {
  try {   
    localStorage.setItem(MW_OPTIONAL_COLS_LS_KEY, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}

function MwColCheckMark() {
  return (
    <svg className="mw-columns-row__check-svg" width="14" height="14" viewBox="0 0 22 22" aria-hidden>
      <path
        d="M5 11.2l3.4 3.4L17 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Toolbar: add / remove watchlist columns */
function MwColumnsToolbarIcon() {
  return (
    <svg className="mw-columns-toolbar-btn__icon" width="18" height="18" viewBox="0 0 18 18" aria-hidden>
      <rect x="1.5" y="3" width="4" height="12" rx="1" fill="currentColor" opacity="0.95" />
      <rect x="7" y="3" width="4" height="12" rx="1" fill="currentColor" opacity="0.55" />
      <rect x="12.5" y="3" width="4" height="12" rx="1" fill="currentColor" opacity="0.3" />
    </svg>
  );
}

function renderMwOptionalColCells(colDefs, row, ui, fmt, fmtInt) {
  if (!colDefs?.length) return null;
  const chgPct = row?.changePercent ?? 0;
  const chgColor = row?.changeAmountColor ?? "";
  const chgN = Number(chgPct);
  const chgFg =
    chgColor === "text-success" || (Number.isFinite(chgN) && chgN > 0)
      ? "#0066b3"
      : chgColor === "text-danger" ||
          chgColor === "text-error" ||
          (Number.isFinite(chgN) && chgN < 0)
        ? "#c41e3a"
        : "#6b7280";
  const numBase = {
    minWidth: 0,
    width: "100%",
    overflow: "visible",
    whiteSpace: "nowrap",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  };
  return colDefs.map((col) => {
    switch (col.id) {
      case "chgPct":
        return (
          <div key={col.id} style={{ ...numBase, fontSize: 12, color: chgFg, fontWeight: 600 }}>
            {Number.isFinite(chgN) ? `${chgN.toFixed(2)}%` : "0.00%"}
          </div>
        );
      case "netChg":
        return (
          <div key={col.id} style={{ ...numBase, fontSize: 12, color: chgFg, fontWeight: 600 }}>
            {fmt(row?.changeAmount, 2)}
          </div>
        );
      case "open":
        return (
          <div key={col.id} style={{ ...numBase, color: ui.mutedText }}>{fmt(row?.open ?? 0)}</div>
        );
      case "high":
        return (
          <div key={col.id} style={{ ...numBase, color: ui.mutedText }}>{fmt(row?.high ?? 0)}</div>
        );
      case "low":
        return (
          <div key={col.id} style={{ ...numBase, color: ui.mutedText }}>{fmt(row?.low ?? 0)}</div>
        );
      case "prevClose":
        return (
          <div key={col.id} style={{ ...numBase, color: ui.mutedText }}>
            {fmt(row?.close ?? row?.previousClose ?? 0)}
          </div>
        );
      case "volume":
        return (
          <div key={col.id} style={{ ...numBase, color: ui.mutedText, fontWeight: 600 }}>
            {fmtInt(row?.volume ?? 0)}
          </div>
        );
      default:
        return null;
    }
  });
}

const MarketWatch = forwardRef(function MarketWatch(
  {
    setPanelExpanded,
    darkTheme = false,
    selectedChartSymbol,
    onSymbolSelect,
    selectedChartRow,
    onSymbolSelectWithRow,
    onOpenQuickOrder,
    onBidAskOpenOrder,
    viewOnly = false,
    /** Shell min width (Symbol+Bid+Ask); keeps density “full” while panel is above this minus a buffer */
    watchlistMinWidthPx = 358,
    onRequestHideWatchlistPanel,
    /** Mobile full-screen watchlist overlay (footer Watch tab) */
    mobileFullscreen = false,
    /** Mobile Quotes chrome: parent shows + / back; notified when Add-symbol browse is open */
    onBrowseModeChange = null,
  },
  ref
) {
  const dispatch = useDispatch();
  const { t } = useAppLanguage();

  const marketWatchList    = useSelector((s) => s.marketWatch?.marketWatchList);
  const marketWatchLoading = useSelector((s) => s.marketWatch?.marketWatchLoading);
  const scriptIndexList    = useSelector((s) => s.marketWatch?.scriptIndexList);

  const session = useMemo(() => getSessionData(), []);
  const userId =
    session?.userId ?? session?.id ?? session?.UserId ?? session?.UserID ?? null;
  const tickCacheKey = useMemo(() => tickCacheStorageKey(userId), [userId]);
  const tickCacheRef = useRef(null);
  const tickCacheFlushRef = useRef(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(tickCacheKey);
      tickCacheRef.current = raw ? JSON.parse(raw) : {};
      if (!tickCacheRef.current || typeof tickCacheRef.current !== "object") {
        tickCacheRef.current = {};
      }
    } catch {
      tickCacheRef.current = {};
    }
    return () => {
      if (tickCacheFlushRef.current) clearTimeout(tickCacheFlushRef.current);
    };
  }, [tickCacheKey]);

  const recordTickToCache = useCallback(
    (row, extraKeys = []) => {
      if (!row || typeof row !== "object") return;
      const ltp = Number(row.lastTradedPrice ?? row.ltp ?? row.LTP ?? 0);
      if (!Number.isFinite(ltp) || ltp <= 0) return;
      const bid = Number(row.buyPrice ?? row.bid ?? 0);
      const ask = Number(row.sellPrice ?? row.ask ?? 0);
      const prevClose = Number(row.__stablePrevClose ?? row.close ?? row.prevClose ?? 0);
      const keys = new Set();
      const add = (x) => {
        const k = normalizeTickKey(x);
        if (k) keys.add(k);
        const first = String(x ?? "").trim().split(/\s+/)[0];
        const kFirst = normalizeTickKey(first);
        if (kFirst) keys.add(kFirst);
      };
      add(row.displayName);
      add(row.script);
      add(row.symbol);
      add(row.name);
      extraKeys.forEach(add);
      const cache = tickCacheRef.current && typeof tickCacheRef.current === "object" ? tickCacheRef.current : {};
      const now = Date.now();
      keys.forEach((k) => {
        if (!k) return;
        cache[k] = {
          ltp,
          bid,
          ask,
          prevClose,
          changeAmount: row.changeAmount,
          changePercent: row.changePercent,
          changeAmountColor: row.changeAmountColor,
          ts: now,
        };
      });
      tickCacheRef.current = cache;

      if (tickCacheFlushRef.current) return;
      tickCacheFlushRef.current = setTimeout(() => {
        tickCacheFlushRef.current = null;
        try {
          localStorage.setItem(tickCacheKey, JSON.stringify(tickCacheRef.current || {}));
        } catch {}
      }, 500);
    },
    [tickCacheKey]
  );

  const [view, setView]                       = useState("watchlist");
  const [searchText, setSearchText]           = useState("");
  const [mwSortObj, setMwSortObj]             = useState(null);
  const [mwColTip, setMwColTip]               = useState(null);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const browseMode = mobileFullscreen && view !== "watchlist";

  useEffect(() => {
    if (!mobileFullscreen || typeof onBrowseModeChange !== "function") return;
    onBrowseModeChange(browseMode);
  }, [mobileFullscreen, browseMode, onBrowseModeChange]);
  const [segments, setSegments]               = useState([]);
  const [symbols, setSymbols]                 = useState([]);
  const [symbolsLoading, setSymbolsLoading]   = useState(false);
  const [addingSymbol, setAddingSymbol]       = useState(new Set());
  const [deletingSymbol, setDeletingSymbol]   = useState(new Set());
  const [isExpanded, setIsExpanded]           = useState(false);
  const [mwOptionalColIds, setMwOptionalColIds] = useState(() => loadMwOptionalColIds());
  const [mwColumnsPageOpen, setMwColumnsPageOpen] = useState(false);
  const [globalScriptHits, setGlobalScriptHits] = useState([]);
  const [globalSearchLoading, setGlobalSearchLoading] = useState(false);
  const [ctxMenu, setCtxMenu]                 = useState(null);
  const [rowActionSheet, setRowActionSheet]   = useState(null);
  /** Last row the user tapped. Stays highlighted until another row is tapped. */
  const [pickedChartKey, setPickedChartKey]   = useState(null);
  const [clearWatchlistOpen, setClearWatchlistOpen] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);

  /** Live quotes for the open row-action sheet (same Redux merge as quick-order panel). */
  const rowActionLiveRow = useMemo(() => {
    if (!rowActionSheet?.row) return null;
    return resolveFreshWatchRow(rowActionSheet.row, marketWatchList);
  }, [rowActionSheet, marketWatchList]);

  const [specRow, setSpecRow]                 = useState(null);
  /** Pointer-based reorder (replaces HTML5 DnD — smoother, fewer browser quirks) */
  const [dragOverIdx, setDragOverIdx]         = useState(null);
  const [dragSourceIdx, setDragSourceIdx]     = useState(null);
  const mwDragPtrRef = useRef(null); // { pointerId, fromIdx }
  const mwReorderOverRef = useRef(null);
  const mwOverHitRafRef = useRef(null);
  const mwOverHitClientRef = useRef(null);
  const moveRowOrderRef = useRef(() => {});
  const longPressRef = useRef(null);
  const bidAskTapRef = useRef({ key: "", t: 0 });
  const bidAskTapTimerRef = useRef(null);
  /** Cache full symbol lists per segment for global search fallback (same sources as loadSymbols). */
  const segmentSymbolsSearchCacheRef = useRef(new Map());
  /** `segmentId|symbol` → expiry suffix e.g. `28Mar2025` for browse / global-search rows */
  const browseExpirySuffixRef = useRef({});
  const [browseExpiryVersion, setBrowseExpiryVersion] = useState(0);
  const [segmentTotalsVersion, setSegmentTotalsVersion] = useState(0);
  const marketWatchListRef = useRef(marketWatchList);
  useEffect(() => { marketWatchListRef.current = marketWatchList; }, [marketWatchList]);

  const scriptIndexListRef = useRef(scriptIndexList);
  useEffect(() => { scriptIndexListRef.current = scriptIndexList; }, [scriptIndexList]);

  const prevRoomRef    = useRef("");
  const prevIdxRoomRef = useRef("");
  // Keep a ref to the active socket so we can re-register handlers after probe swap
  const sockRef = useRef(null);
  const tableScrollRef = useRef(null);
  const mt5HeaderScrollRef = useRef(null);
  const mt5HeaderBodySyncRef = useRef(false);
  const mobileTailHeadScrollRef = useRef(null);
  const mobileTailBodyScrollRef = useRef(null);
  const mobilePinnedBodyScrollRef = useRef(null);
  const mt5FullHScrollRef = useRef(null);
  const mobileTailScrollSyncRef = useRef(false);

  const syncMt5HeaderScrollToBody = useCallback((e) => {
    if (mt5HeaderBodySyncRef.current) return;
    const body = tableScrollRef.current;
    if (!body) return;
    mt5HeaderBodySyncRef.current = true;
    body.scrollLeft = e.target.scrollLeft;
    requestAnimationFrame(() => {
      mt5HeaderBodySyncRef.current = false;
    });
  }, []);

  const syncMt5BodyScrollToHeader = useCallback((e) => {
    if (mt5HeaderBodySyncRef.current) return;
    const head = mt5HeaderScrollRef.current;
    if (!head) return;
    mt5HeaderBodySyncRef.current = true;
    head.scrollLeft = e.target.scrollLeft;
    requestAnimationFrame(() => {
      mt5HeaderBodySyncRef.current = false;
    });
  }, []);

  const syncMobileTailScrollFromHead = useCallback((e) => {
    if (mobileTailScrollSyncRef.current) return;
    const body = mobileTailBodyScrollRef.current;
    const fullH = mt5FullHScrollRef.current;
    if (!body && !fullH) return;
    mobileTailScrollSyncRef.current = true;
    if (body) body.scrollLeft = e.target.scrollLeft;
    if (fullH) fullH.scrollLeft = e.target.scrollLeft;
    requestAnimationFrame(() => {
      mobileTailScrollSyncRef.current = false;
    });
  }, []);

  /** Tail pane: H → header + full bar, V → pinned Symbol. */
  const syncMobileTailScrollFromBody = useCallback((e) => {
    if (mobileTailScrollSyncRef.current) return;
    const head = mobileTailHeadScrollRef.current;
    const pinned = mobilePinnedBodyScrollRef.current;
    const fullH = mt5FullHScrollRef.current;
    mobileTailScrollSyncRef.current = true;
    if (head) head.scrollLeft = e.target.scrollLeft;
    if (pinned) pinned.scrollTop = e.target.scrollTop;
    if (fullH) fullH.scrollLeft = e.target.scrollLeft;
    requestAnimationFrame(() => {
      mobileTailScrollSyncRef.current = false;
    });
  }, []);

  /** Full-width H scrollbar → price header + body (Symbol stays pinned). */
  const syncMt5FullHScroll = useCallback((e) => {
    if (mobileTailScrollSyncRef.current) return;
    const body = mobileTailBodyScrollRef.current;
    const head = mobileTailHeadScrollRef.current;
    mobileTailScrollSyncRef.current = true;
    if (body) body.scrollLeft = e.target.scrollLeft;
    if (head) head.scrollLeft = e.target.scrollLeft;
    requestAnimationFrame(() => {
      mobileTailScrollSyncRef.current = false;
    });
  }, []);

  /** Wheel/scroll on Symbol column keeps rows aligned with price pane. */
  const syncMobilePinnedBodyScroll = useCallback((e) => {
    if (mobileTailScrollSyncRef.current) return;
    const body = mobileTailBodyScrollRef.current;
    if (!body) return;
    mobileTailScrollSyncRef.current = true;
    body.scrollTop = e.target.scrollTop;
    requestAnimationFrame(() => {
      mobileTailScrollSyncRef.current = false;
    });
  }, []);

  const cancelMwOverHitRaf = useCallback(() => {
    if (mwOverHitRafRef.current != null) {
      cancelAnimationFrame(mwOverHitRafRef.current);
      mwOverHitRafRef.current = null;
    }
    mwOverHitClientRef.current = null;
  }, []);

  /** One hit-test per frame while dragging — fast scroll, no setState flood */
  const scheduleMwOverHitTest = useCallback(
    (clientX, clientY) => {
      mwOverHitClientRef.current = { clientX, clientY };
      if (mwOverHitRafRef.current != null) return;
      mwOverHitRafRef.current = requestAnimationFrame(() => {
        mwOverHitRafRef.current = null;
        const p = mwOverHitClientRef.current;
        if (!p || !mwDragPtrRef.current) return;
        const stack = document.elementsFromPoint(p.clientX, p.clientY);
        let over = null;
        for (const el of stack) {
          if (!(el instanceof Element)) continue;
          const row = el.closest(".mw-watchlist-row");
          const raw = row?.getAttribute("data-mw-idx");
          if (raw != null && raw !== "") {
            const i = parseInt(raw, 10);
            if (Number.isFinite(i)) {
              over = i;
              break;
            }
          }
        }
        if (over !== mwReorderOverRef.current) {
          mwReorderOverRef.current = over;
          setDragOverIdx(over);
        }
      });
    },
    []
  );

  useEffect(() => () => cancelMwOverHitRaf(), [cancelMwOverHitRaf]);

  const watchRows = useMemo(() => {
    if (!marketWatchList) return [];
    if (marketWatchList instanceof Map) return Array.from(marketWatchList.values());
    if (Array.isArray(marketWatchList)) return marketWatchList;
    // Immer edge / bad shape: plain object — still show rows instead of false "empty" state
    if (typeof marketWatchList === "object") {
      const vals = Object.values(marketWatchList);
      if (vals.length && vals.every((v) => v != null && typeof v === "object")) return vals;
    }
    return [];
  }, [marketWatchList]);

  /** Serialize watchlist refetches so rapid add/remove don't overlap and drop updates */
  const mwRefreshChainRef = useRef(Promise.resolve());
  const refreshMarketWatchList = useCallback(() => {
    const next = mwRefreshChainRef.current.then(() =>
      dispatch(getMarketWatchList({ isRefresh: true })).unwrap()
    );
    mwRefreshChainRef.current = next.catch(() => {});
    return next;
  }, [dispatch]);

  const filteredWatchRows = useMemo(() => {
    if (!searchText || view !== "watchlist") return watchRows;
    const q = searchText.trim().toLowerCase();
    return watchRows.filter((r) =>
      [r?.script, r?.displayName, r?.symbol, r?.name, formatWatchlistRowLabel(r)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [watchRows, searchText, view]);

  const sortedWatchRows = useMemo(() => {
    if (!mwSortObj) return filteredWatchRows;
    const { id, desc } = mwSortObj;
    const dir = desc ? -1 : 1;
    return [...filteredWatchRows].sort((a, b) => {
      const av = mwSortValue(a, id);
      const bv = mwSortValue(b, id);
      if (typeof av === "string" || typeof bv === "string") {
        return (
          String(av).localeCompare(String(bv), undefined, {
            numeric: true,
            sensitivity: "base",
          }) * dir
        );
      }
      const an = Number(av);
      const bn = Number(bv);
      const aOk = Number.isFinite(an);
      const bOk = Number.isFinite(bn);
      if (!aOk && !bOk) return 0;
      if (!aOk) return 1;
      if (!bOk) return -1;
      if (an === bn) return 0;
      return an > bn ? dir : -dir;
    });
  }, [filteredWatchRows, mwSortObj]);

  const handleMwSort = useCallback((key) => {
    setMwSortObj((prev) => {
      if (prev?.id !== key) return { id: key, desc: false };
      if (!prev.desc) return { id: key, desc: true };
      return null;
    });
  }, []);

  const hideMwColTip = useCallback(() => setMwColTip(null), []);

  const showMwColTip = useCallback((e, text) => {
    const el = e.currentTarget;
    if (!(el instanceof Element) || !text) return;
    const r = el.getBoundingClientRect();
    setMwColTip({
      text,
      x: Math.round(r.left + 4),
      y: Math.round(r.bottom + 3),
    });
  }, []);

  const mwSortMark = (id) => {
    if (!mwSortObj || mwSortObj.id !== id) return null;
    return (
      <span
        className={`mw-wl-sort mw-wl-sort--${mwSortObj.desc ? "desc" : "asc"}`}
        aria-hidden
      />
    );
  };

  const renderMwColHead = (id, label, tip, extra = {}) => {
    const { className = "", style, alignEnd = false } = extra;
    const tipText = tip || label;
    return (
      <div
        className={["mw-wl-sortable", alignEnd ? "mw-wl-sortable--end" : "", className]
          .filter(Boolean)
          .join(" ")}
        style={style}
        onMouseEnter={(e) => showMwColTip(e, tipText)}
        onMouseLeave={hideMwColTip}
        onClick={() => handleMwSort(id)}
      >
        {mwSortMark(id)}
        <span className="mw-wl-hlabel">{label}</span>
      </div>
    );
  };

  const hasLoadedRef = useRef(false);
  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    dispatch(getIndexScript());
    refreshMarketWatchList().catch(() => {});
    // Start socket probe immediately
    getSocket();
  }, [dispatch, refreshMarketWatchList]);

  const toggleExpanded = useCallback((e) => {
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();
    // Always show watchlist immediately when toggling expand/collapse.
    setView("watchlist");
    setIsExpanded((prev) => {
      const next = !prev;
      if (typeof setPanelExpanded === "function") setPanelExpanded(next);
      // When expanding, jump horizontal scroll back to start so Symbol/Bid/Ask/LTP are visible.
      if (next) {
        setTimeout(() => {
          if (tableScrollRef.current) tableScrollRef.current.scrollLeft = 0;
        }, 0);
      }
      return next;
    });
  }, [setPanelExpanded]);

  // Snap to left when entering expanded mode so Symbol/Bid/Ask/LTP are visible.
  useEffect(() => {
    if (!isExpanded) return;
    const el = tableScrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => { el.scrollLeft = 0; });
  }, [isExpanded]);

  // MetaTrader-style quotes UI for desktop dock + mobile fullscreen (presentation only).
  const mt5Ui = true;

  const activeOptionalColDefs = useMemo(() => {
    if (mobileFullscreen) {
      // Mobile Quotes is fixed: Symbol | Bid | Ask | LTP.
      return [];
    }
    if (isExpanded) {
      // Expanded: all optionals; Daily Change is already a fixed column in collapsed MT5 UI.
      return MW_OPTIONAL_WATCH_COLS.filter((c) => c.id !== "chgPct");
    }
    return [];
  }, [mobileFullscreen, mwOptionalColIds, isExpanded]);

  const optionalColCount = activeOptionalColDefs.length;
  const showOptionalCols = optionalColCount > 0;
  const mobileHasOptionalCols = mobileFullscreen && showOptionalCols;
  /** Collapsed MT5 quotes: Symbol / Bid / Ask / LTP (delete via sheet/context). */
  const mt5CollapsedQuotes = mt5Ui && !(isExpanded && !mobileFullscreen);

  // Desktop: full-height Symbol freeze line only when the list is short, a
  // horizontal scrollbar is showing, and Bid has scrolled out of view.
  useEffect(() => {
    const freeze = mt5CollapsedQuotes && !mobileFullscreen;
    const el = tableScrollRef.current;
    if (!el || !freeze || view !== "watchlist") {
      el?.classList.remove("mw-watchlist-scroll--sym-v-edge");
      return undefined;
    }

    const update = () => {
      const node = tableScrollRef.current;
      if (!node) return;

      const tail =
        node.querySelector(".mw-mobile-wl-tail-scroll--body") ||
        node.querySelector(".mw-mobile-wl-tail-scroll--header");
      const pinned = node.querySelector(".mw-mobile-wl-pinned--body");
      const isSplit = Boolean(tail && pinned);

      let shortList = false;
      let hScroll = false;
      let bidHidden = false;

      if (isSplit) {
        const lastRow = pinned.querySelector(".mw-watchlist-row:last-child");
        const pinBox = pinned.getBoundingClientRect();
        shortList = lastRow
          ? lastRow.getBoundingClientRect().bottom < pinBox.bottom - 8
          : true;
        hScroll = tail.scrollWidth > tail.clientWidth + 1;
        const bidHead = node.querySelector(
          ".mw-mobile-wl-tail-scroll--header .mw-wl-col--price"
        );
        const tailBox = (
          node.querySelector(".mw-mobile-wl-tail-scroll--header") || tail
        ).getBoundingClientRect();
        if (bidHead) {
          // Bid is not fully shown once it starts clipping under the Symbol edge.
          bidHidden = bidHead.getBoundingClientRect().left < tailBox.left - 1;
        } else {
          bidHidden = tail.scrollLeft > 2;
        }
      } else {
        shortList = node.scrollHeight <= node.clientHeight + 1;
        hScroll = node.scrollWidth > node.clientWidth + 1;
        if (shortList && hScroll && node.scrollLeft > 0) {
          const header = node.querySelector(".mw-watchlist-header-row--mt5-in-scroll");
          const bid = header?.querySelector(".mw-wl-col--price");
          const sym = header?.querySelector(".mw-wl-sticky--sym");
          if (bid && sym) {
            bidHidden =
              bid.getBoundingClientRect().left <
              sym.getBoundingClientRect().right - 1;
          } else {
            bidHidden = node.scrollLeft > 2;
          }
        }
      }

      node.classList.toggle(
        "mw-watchlist-scroll--sym-v-edge",
        shortList && hScroll && bidHidden
      );
    };

    update();
    const scrollEls = [
      el,
      el.querySelector(".mw-mobile-wl-tail-scroll--body"),
      el.querySelector(".mw-mobile-wl-tail-scroll--header"),
    ].filter(Boolean);
    scrollEls.forEach((node) => node.addEventListener("scroll", update, { passive: true }));
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    scrollEls.forEach((node) => ro?.observe(node));
    const pinnedBody = el.querySelector(".mw-mobile-wl-pinned--body");
    if (pinnedBody) ro?.observe(pinnedBody);
    return () => {
      scrollEls.forEach((node) => node.removeEventListener("scroll", update));
      ro?.disconnect();
      el.classList.remove("mw-watchlist-scroll--sym-v-edge");
    };
  }, [mt5CollapsedQuotes, mobileFullscreen, view, isExpanded, sortedWatchRows.length]);

  const toggleMwOptionalCol = useCallback((colId) => {
    setMwOptionalColIds((prev) => {
      const set = new Set(prev);
      if (set.has(colId)) set.delete(colId);
      else set.add(colId);
      const next = MW_OPTIONAL_COL_IDS.filter((id) => set.has(id));
      saveMwOptionalColIds(next);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!mwColumnsPageOpen) return;
    const onKey = (ev) => {
      if (ev.key === "Escape") setMwColumnsPageOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mwColumnsPageOpen]);

  useEffect(() => {
    if (!mobileFullscreen) return;
    const reset = () => {
      if (mobileTailBodyScrollRef.current) mobileTailBodyScrollRef.current.scrollLeft = 0;
      if (mobileTailHeadScrollRef.current) mobileTailHeadScrollRef.current.scrollLeft = 0;
      if (mt5FullHScrollRef.current) mt5FullHScrollRef.current.scrollLeft = 0;
    };
    requestAnimationFrame(reset);
  }, [mobileFullscreen, optionalColCount]);

  // ── Core message handler (created once, used by any socket version) ───────
  const handleMessageRef = useRef(null);
  useEffect(() => {
    handleMessageRef.current = (raw) => {
      const data = normalizeTickPayload(raw);
      if (!data || Object.keys(data).length === 0) return;

      // Normalize field names — indices often arrive as instrument token, not display name
      const Script =
        data.Script ??
        data.script ??
        data.SCRIPT ??
        data.displayName ??
        data.DisplayName ??
        data.name ??
        data.Name ??
        data.Symbol ??
        data.symbol ??
        data.Token ??
        data.token ??
        data.instrumentToken ??
        data.InstrumentToken ??
        "";
      const scriptStr = String(Script).trim();
      if (!scriptStr) return;

      const LTP       = Number(
        data.LTP ??
        data.ltp ??
        data.Ltp ??
        data.lastTradedPrice ??
        data.LastTradedPrice ??
        data.lastPrice ??
        data.LastPrice ??
        data.close ??
        data.Close ??
        0
      );
      const Bid       = Number(data.Bid       ?? data.bid       ?? data.BidRate   ?? data.buyPrice  ?? 0);
      const Ask       = Number(data.Ask       ?? data.ask       ?? data.AskRate   ?? data.sellPrice ?? 0);
      const PrevClose = Number(
        data.PrevClose ??
        data.prevClose ??
        data.prev_close ??
        data.previousClose ??
        data.PreviousClose ??
        data.close ??
        data.Close ??
        0
      );
      const Low       = Number(data.Low       ?? data.low       ?? 0);
      const High      = Number(data.High      ?? data.high      ?? 0);
      const Open      = Number(data.Open      ?? data.open      ?? 0);
      const Vol       = data.Vol ?? data.vol ?? data.Volume ?? data.volume ?? 0;

      const tickCandidates = new Set();
      const addTick = (x) => {
        if (x == null || x === "") return;
        const s = String(x).trim();
        if (!s) return;
        tickCandidates.add(s);
        tickCandidates.add(s.toUpperCase());
      };
      addTick(scriptStr);
      addTick(Script);
      addTick(data.Script);
      addTick(data.script);
      addTick(data.Symbol);
      addTick(data.symbol);
      addTick(data.Token);
      addTick(data.token);
      addTick(data.instrumentToken);
      addTick(data.InstrumentToken);
      const tickBenchmarkAlias = canonicalBenchmarkIndexAlias(scriptStr);

      const resolveStablePrevClose = (row) => {
        const fromRow = Number(row?.__stablePrevClose ?? 0);
        if (Number.isFinite(fromRow) && fromRow > 0) return fromRow;
        const picked = pickReasonablePrevClose(LTP, PrevClose, row);
        return Number.isFinite(picked) && picked > 0 ? picked : 0;
      };
      const chgFmt = (row) => {
        const pc = resolveStablePrevClose(row);
        const chg = LTP - pc;
        return {
          prevClose: pc,
          changeAmount: chg.toFixed(2),
          changeAmountColor: chg >= 0 ? "text-success" : "text-danger",
          changePercent:
            LTP && pc && pc !== 0 ? (((LTP - pc) / pc) * 100).toFixed(2) : "0.00",
        };
      };

      // 1. Watchlist first — avoids index fuzzy matching stealing ticks for symbols like BANKNIFTY futures.
      const currentMap = marketWatchListRef.current;
      if (currentMap instanceof Map && currentMap.size > 0) {
        const wlNorm = scriptStr.toUpperCase();
        const scriptKey = wlNorm.replace(/\s+/g, "");
        let existingKey = null;
        let existingRow = null;
        if (currentMap.has(Script) || currentMap.has(scriptStr)) {
          existingKey = currentMap.has(Script) ? Script : scriptStr;
          existingRow = currentMap.get(existingKey);
        } else {
          for (const [k, v] of currentMap) {
            const d = (v?.displayName || "").toString().trim().toUpperCase();
            const s = (v?.script || "").toString().trim().toUpperCase();
            const sym = (v?.symbol || "").toString().trim().toUpperCase();
            const dKey = d.replace(/\s+/g, "");
            const sKey = s.replace(/\s+/g, "");
            const symKey = sym.replace(/\s+/g, "");
            const tokenHit = indexRowIdentityTokens(v).some(
              (id) => tickCandidates.has(id) || tickCandidates.has(id.toUpperCase())
            );
            const match =
              tokenHit ||
              v?.displayName === Script ||
              v?.script === Script ||
              v?.symbol === Script ||
              v?.displayName === scriptStr ||
              v?.script === scriptStr ||
              v?.symbol === scriptStr ||
              wlNorm === d ||
              wlNorm === s ||
              wlNorm === sym ||
              scriptKey === dKey ||
              scriptKey === sKey ||
              scriptKey === symKey ||
              namesShareScriptFamily(wlNorm, d) ||
              namesShareScriptFamily(wlNorm, s) ||
              namesShareScriptFamily(wlNorm, sym);
            if (match) {
              existingKey = k;
              existingRow = v;
              break;
            }
          }
        }

        if (existingKey && existingRow) {
          const { lastTradedPrice: prevLTP, buyPrice: prevBid, sellPrice: prevAsk } = existingRow;
          const chg = chgFmt(existingRow);
          let syncedBenchmarkIndex = false;

          const updatedRow = {
            ...existingRow,
            lastTradedPrice: LTP,
            buyPrice: Bid,
            sellPrice: Ask,
            volume: Vol ? parseFloat(Vol) : existingRow.volume ?? 0,
            low: Low,
            high: High,
            open: Open,
            close: chg.prevClose,
            __stablePrevClose: chg.prevClose,
            colorLTP: prevLTP > LTP ? MW_MT5_TICK_DOWN : prevLTP < LTP ? MW_MT5_TICK_UP : existingRow.colorLTP || "",
            colorBid: prevBid > Bid ? MW_MT5_TICK_DOWN : prevBid < Bid ? MW_MT5_TICK_UP : existingRow.colorBid || "",
            colorAsk: prevAsk > Ask ? MW_MT5_TICK_DOWN : prevAsk < Ask ? MW_MT5_TICK_UP : existingRow.colorAsk || "",
            changeAmount: chg.changeAmount,
            changeAmountColor: chg.changeAmountColor,
            changePercent: chg.changePercent,
            quoteTime:
              parseQuoteTime(data.Ltt ?? data.ltt ?? data.LastTradeTime ?? data.lastTradeTime ?? data.Time ?? data.time) ||
              formatHms(),
            isUpdated: true,
          };
          recordTickToCache(updatedRow, [existingKey, scriptStr]);

          const newMap = new Map(currentMap);
          newMap.set(existingKey, updatedRow);
          dispatch(setMarketWatchList(newMap));

          const idxMapSync = scriptIndexListRef.current;
          if (idxMapSync instanceof Map && idxMapSync.size > 0) {
            let syncKey = null;
            let syncRow = null;
            const dn = String(existingRow.displayName || "").trim();
            const sc = String(existingRow.script || "").trim();
            const sy = String(existingRow.symbol || "").trim();
            const wlToks = indexRowIdentityTokens(existingRow).map((t) => String(t).trim().toUpperCase());
            const wlTokSet = new Set(wlToks);
            for (const [k, v] of idxMapSync) {
              const hitTok = indexRowIdentityTokens(v).some((id) => {
                const u = String(id).trim().toUpperCase();
                return wlTokSet.has(u) || wlTokSet.has(String(id).trim());
  }, []);
              if (hitTok) {
                syncKey = k;
                syncRow = v;
                break;
              }
            }
            if (!syncKey) {
              for (const [k, v] of idxMapSync) {
                const vd = String(v?.displayName || "").trim();
                const vs = String(v?.script || "").trim();
                const vy = String(v?.symbol || "").trim();
                if (
                  (dn && (vd === dn || vs === dn || vy === dn)) ||
                  (sc && (vd === sc || vs === sc || vy === sc)) ||
                  (sy && (vd === sy || vs === sy || vy === sy))
                ) {
                  syncKey = k;
                  syncRow = v;
                  break;
                }
              }
            }
            if (syncKey && syncRow) {
              const prevIL = Number(syncRow.lastTradedPrice ?? 0);
              const chgI = chgFmt(syncRow);
              const updatedIdx = {
                ...syncRow,
                lastTradedPrice: LTP,
                close: chgI.prevClose,
                __stablePrevClose: chgI.prevClose,
                colorLTP: prevIL > LTP ? MW_MT5_TICK_DOWN : prevIL < LTP ? MW_MT5_TICK_UP : syncRow.colorLTP || "",
                changeAmount: chgI.changeAmount,
                changeAmountColor: chgI.changeAmountColor,
                changePercent: chgI.changePercent,
              };
              const idxAlias = tickBenchmarkAlias ? `IDX:${tickBenchmarkAlias}` : null;
              recordTickToCache(updatedIdx, [syncKey, idxAlias].filter(Boolean));
              const newIdxMap = new Map(idxMapSync);
              newIdxMap.set(syncKey, updatedIdx);
              dispatch(setScriptIndexList(newIdxMap));
              syncedBenchmarkIndex = true;
            }
          }
          // If this tick is for benchmark index alias and sync failed through watchlist path,
          // continue to dedicated benchmark path below instead of returning early.
          if (!tickBenchmarkAlias || syncedBenchmarkIndex) return;
        }
      }

      // 2. Benchmark indices only (exact / token / id — no substring fuzzy: it mis-routed ticks).
      const idxMap = scriptIndexListRef.current;
      if (idxMap instanceof Map && idxMap.size > 0) {
        let idxKey = null;
        let idxRow = null;
        for (const [k, v] of idxMap) {
          const ids = indexRowIdentityTokens(v);
          for (const id of ids) {
            const u = id.toUpperCase();
            if (tickCandidates.has(id) || tickCandidates.has(u)) {
              idxKey = k;
              idxRow = v;
              break;
            }
          }
          if (idxKey) break;
        }

        // Fallback for index aliases (e.g. NIFTY vs NIFTY 50, NIFTYBANK vs BNIFTY).
        if (!idxKey && tickBenchmarkAlias) {
          for (const [k, v] of idxMap) {
            if (rowBenchmarkIndexAlias(v) === tickBenchmarkAlias) {
              idxKey = k;
              idxRow = v;
              break;
            }
          }
        }

        if (!idxKey || !idxRow) {
          if (idxMap.has(Script) || idxMap.has(scriptStr)) {
            idxKey = idxMap.has(Script) ? Script : scriptStr;
            idxRow = idxMap.get(idxKey);
          } else {
            for (const [k, v] of idxMap) {
              if (
                v?.displayName === Script ||
                v?.script === Script ||
                k === Script ||
                v?.displayName === scriptStr ||
                v?.script === scriptStr ||
                k === scriptStr
              ) {
                idxKey = k;
                idxRow = v;
                break;
              }
            }
          }
        }
        if (!idxKey || !idxRow) {
          const scriptId = scriptStr;
          for (const [k, v] of idxMap) {
            if (v?.id != null && String(v.id) === scriptId) {
              idxKey = k;
              idxRow = v;
              break;
            }
          }
        }
        if (idxKey && idxRow) {
          const prevLTP = idxRow.lastTradedPrice ?? 0;
          const chg = chgFmt(idxRow);
          const updated = {
            ...idxRow,
            lastTradedPrice: LTP,
            close: chg.prevClose,
            __stablePrevClose: chg.prevClose,
            colorLTP: prevLTP > LTP ? MW_MT5_TICK_DOWN : prevLTP < LTP ? MW_MT5_TICK_UP : idxRow.colorLTP || "",
            changeAmount: chg.changeAmount,
            changeAmountColor: chg.changeAmountColor,
            changePercent: chg.changePercent,
          };
          const idxAlias = tickBenchmarkAlias ? `IDX:${tickBenchmarkAlias}` : null;
          recordTickToCache(updated, [idxKey, idxAlias].filter(Boolean));
          const newIdx = new Map(idxMap);
          newIdx.set(idxKey, updated);
          dispatch(setScriptIndexList(newIdx));
        }
      }
    };
  }); // runs every render so closure is always fresh — handler reads via ref anyway

  // ── Register socket handlers ───────────────────────────────────────────────
  const registerHandlers = useCallback((sock) => {
    if (!sock || sock._probing) return;

    // Remove old handlers first to prevent duplicates
    sock.off("message");
    sock.off("tick");
    sock.off("connect");

    const onTick = (data) => {
      if (handleMessageRef.current) handleMessageRef.current(data);
    };
    sock.on("message", onTick);
    sock.on("tick", onTick);

    sock.on("connect", () => {
      if (prevRoomRef.current) sock.emit("room", prevRoomRef.current);
      emitIndexSocketSubscriptions(sock, scriptIndexListRef.current);
    });

    sockRef.current = sock;
  }, []);

  // ── Initial socket handler registration ───────────────────────────────────
  useEffect(() => {
    const sock = getSocket();
    if (!sock?._probing) {
      registerHandlers(sock);
    }

    // Listen for probe completion — socketInstance fires this when it finds the URL
    const onSocketReady = (e) => {
      const { socket: newSock } = e.detail;
     
      registerHandlers(newSock);

      // Re-join rooms with the new real socket
      if (prevRoomRef.current) newSock.emit("room", prevRoomRef.current);
      emitIndexSocketSubscriptions(newSock, scriptIndexListRef.current);
    };

    window.addEventListener("socket-ready", onSocketReady);

    return () => {
      window.removeEventListener("socket-ready", onSocketReady);
      const s = sockRef.current;
      if (s) {
        s.off("message");
        s.off("tick");
        s.off("connect");
        if (prevRoomRef.current)    s.emit("leave_room", prevRoomRef.current);
        if (prevIdxRoomRef.current) s.emit("leave_room", prevIdxRoomRef.current);
      }
    };
  }, [dispatch, registerHandlers]);

  // ── Join rooms when watchlist loads ───────────────────────────────────────
  useEffect(() => {
    if (watchRows.length === 0) return;

    const roomNames = Array.from(
      new Set(
        watchRows
          .flatMap((r) => [r?.displayName, r?.script, r?.symbol])
          .filter((x) => typeof x === "string" && x.trim().length > 0)
          .map((x) => x.trim())
      )
    );
    if (!roomNames.length) return;

    const roomStr = roomNames.toString();
    if (roomStr === prevRoomRef.current) return;

    prevRoomRef.current = roomStr;

    const sock = sockRef.current || getSocket();
    if (sock && !sock._probing) {
      sock.emit("room", roomStr);
      sock.emit("subscribe", { symbols: roomNames });
      
    } else {
      // Will be sent when socket-ready fires
     
    }
  }, [watchRows]);

  // ── Auto-select first symbol for chart when watchlist loads
  useEffect(() => {
    if (watchRows.length === 0) return;
    const firstRow = watchRows[0];
    const first = firstRow?.script ?? firstRow?.symbol ?? firstRow?.displayName;
    if (!first || selectedChartSymbol) return;
    if (onSymbolSelectWithRow) onSymbolSelectWithRow(first, firstRow);
    else if (onSymbolSelect) onSymbolSelect(first);
  }, [watchRows, selectedChartSymbol, onSymbolSelect, onSymbolSelectWithRow]);

  // ── Join index rooms ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!scriptIndexList || scriptIndexList.size === 0) return;
    const tokens = indexRowsSubscriptionTokens(scriptIndexList);
    const names = tokens.toString();
    if (!names || names === prevIdxRoomRef.current) return;
    prevIdxRoomRef.current = names;

    const sock = sockRef.current || getSocket();
    if (sock && !sock._probing) {
      emitIndexSocketSubscriptions(sock, scriptIndexList);
    }
  }, [scriptIndexList]);

  // ── Segments ──────────────────────────────────────────────────────────────
  const loadSegments = useCallback(async () => {
    setSegments([]);
    const session = getSessionData();
    const userId = session?.userId ?? session?.id ?? session?.UserId ?? null;
    try {
      const result = await dispatch(getSegmentOptionList({ force: true, userId }));
      if (
        getSegmentOptionList.fulfilled.match(result) &&
        Array.isArray(result.payload) &&
        result.payload.length > 0
      ) {
        setSegments(result.payload);
      }
    } catch {
      /* ignore */
    }
  }, [dispatch]);

  const openAddSymbol = useCallback(() => {
    setGlobalScriptHits([]);
    setView("segments");
    loadSegments();
  }, [loadSegments]);

  const closeAddSymbol = useCallback(() => {
    setSearchText("");
    setGlobalScriptHits([]);
    setSelectedSegment(null);
    setView("watchlist");
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      openAddSymbol,
      closeAddSymbol,
    }),
    [openAddSymbol, closeAddSymbol]
  );

  const loadSymbols = useCallback(async (seg) => {
    if (!seg?.value) return;
    setSymbolsLoading(true);
    setSymbols([]);
    const segId = String(seg.value);
    const cache = browseExpirySuffixRef.current;

    const bulkPromise = api
      .get(`settings/symbol-expiry/get-symbol-expiry-symbols-by-segment/${seg.value}`)
      .then((res) => {
        const rows = extractExpiryListFromResponse(res);
        ingestBulkSegmentExpiryRows(segId, rows, cache);
      })
      .catch(() => {});

    const finishWithList = async (list) => {
      if (!Array.isArray(list) || list.length === 0) return;
      setSymbols(list);
      setSymbolsLoading(false);
      const segKey = String(seg.value);
      segmentSymbolsSearchCacheRef.current.set(segKey, list);
      setSegmentTotalsVersion((v) => v + 1);
      await bulkPromise;
      const names = list.map(browseRowBaseName).filter(Boolean);
      await fetchExpirySuffixesMissing({ segId, names, cache, concurrency: 28 });
      setBrowseExpiryVersion((v) => v + 1);
    };

    try {
      const res = await api.get(`/market-watch/get-MW-symbols-for-nse/${seg.value}`);
      if (res?.status === 200 && Array.isArray(res?.data?.data) && res.data.data.length > 0) {
        await finishWithList(res.data.data);
        return;
      }
    } catch {
      /* fall through */
    }

    try {
      const a = await dispatch(getSymbolList({ id: seg.value }));
      if (getSymbolList.fulfilled.match(a) && Array.isArray(a.payload) && a.payload.length > 0) {
        await finishWithList(a.payload);
        return;
      }
    } catch {
      /* ignore */
    }
    await bulkPromise.catch(() => {});
    setSymbolsLoading(false);
  }, [dispatch]);

  const filteredSymbols = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return symbols;
    return symbols.filter((x) => {
      const name = browseRowBaseName(x);
      return name.toLowerCase().includes(q);
    });
  }, [symbols, searchText]);

  useEffect(() => {
    if (view !== "symbols" || selectedSegment?.value == null || selectedSegment.value === "") return;
    if (symbols.length === 0) return;
    const segId = String(selectedSegment.value);
    const source = searchText.trim() ? filteredSymbols : symbols;
    const names = [...new Set(source.map(browseRowBaseName).filter(Boolean))].slice(0, 400);
    if (!names.length) return;
    let cancelled = false;
    const cache = browseExpirySuffixRef.current;

    (async () => {
      await fetchExpirySuffixesMissing({ segId, names, cache, concurrency: 28 });
      if (!cancelled) setBrowseExpiryVersion((v) => v + 1);
    })();

    return () => {
      cancelled = true;
    };
  }, [view, selectedSegment?.value, symbols, filteredSymbols, searchText]);

  useEffect(() => {
    if (view !== "segments" || searchText.trim().length < 2 || globalScriptHits.length === 0) return;
    let cancelled = false;
    const cache = browseExpirySuffixRef.current;
    const slice = globalScriptHits.slice(0, 120);
    const bySeg = new Map();
    for (const h of slice) {
      const segId = String(h.segment?.value ?? "");
      const n = h.name;
      if (!segId || !n) continue;
      if (!bySeg.has(segId)) bySeg.set(segId, []);
      bySeg.get(segId).push(n);
    }

    (async () => {
      for (const [segId, names] of bySeg) {
        if (cancelled) return;
        await fetchExpirySuffixesMissing({ segId, names, cache, concurrency: 28 });
      }
      if (!cancelled) setBrowseExpiryVersion((v) => v + 1);
    })();

    return () => {
      cancelled = true;
    };
  }, [view, searchText, globalScriptHits]);

  const isInWatchlist = useCallback((sym) => {
    const name = browseRowBaseName(sym);
    if (!name || watchRows.length === 0) return false;
    const check = (v) =>
      v?.symbol === name || v?.displayName === name ||
      (typeof v?.script === "string" && v.script.startsWith(name)) || v?.name === name;
    return watchRows.some(check);
  }, [watchRows]);

  const segmentSelectedCounts = useMemo(() => {
    const map = new Map();
    for (const row of watchRows) {
      const sid = row?.segmentId ?? row?.SegmentId;
      if (sid == null || sid === "") continue;
      const k = String(sid);
      map.set(k, (map.get(k) || 0) + 1);
    }
    return map;
  }, [watchRows]);

  const getSegmentTotalCount = useCallback(
    (segValue) => {
      void segmentTotalsVersion;
      const key = String(segValue ?? "");
      if (!key) return null;
      const cached = segmentSymbolsSearchCacheRef.current.get(key);
      return Array.isArray(cached) ? cached.length : null;
    },
    [segmentTotalsVersion]
  );

  // MT5 shows selected/total on the category list (e.g. 10/126). Prefetch totals
  // when the Search symbol panel opens so we are not stuck on "3".
  useEffect(() => {
    if (view !== "segments" || !Array.isArray(segments) || segments.length === 0) {
      return undefined;
    }
    let cancelled = false;
    const cache = segmentSymbolsSearchCacheRef.current;
    const pending = segments.filter((seg) => {
      const key = String(seg?.value ?? "");
      return key && !cache.has(key);
    });
    if (pending.length === 0) return undefined;

    (async () => {
      await Promise.all(
        pending.map(async (seg) => {
          const key = String(seg.value);
          let arr = null;
          try {
            const res = await api.get(`/market-watch/get-MW-symbols-for-nse/${seg.value}`);
            if (res?.status === 200 && Array.isArray(res?.data?.data)) arr = res.data.data;
          } catch {
            /* fall through */
          }
          if (arr == null) {
            try {
              const res2 = await api.get(`/market-watch/get-symbols-by-segment/${seg.value}`);
              if (res2?.data?.status === 200 && Array.isArray(res2.data.data)) {
                arr = res2.data.data;
              }
            } catch {
              /* ignore */
            }
          }
          if (!cancelled && Array.isArray(arr)) cache.set(key, arr);
        })
      );
      if (!cancelled) setSegmentTotalsVersion((v) => v + 1);
    })();

    return () => {
      cancelled = true;
    };
  }, [view, segments]);

  const addSymbolForSegment = useCallback(
    async (seg, sym, opts = {}) => {
      const stayOnView = opts.stayOnView === true;
      const symName = typeof sym === "string" ? sym : browseRowBaseName(sym);
      if (!symName || !seg?.value) return;
      if (addingSymbol.has(symName)) return;
      setAddingSymbol(prev => { const n = new Set(prev); n.add(symName); return n; });
      const session = getSessionData();
      const userId = session.userId || session.UserId || session.id || 0;
      const segmentId = Number(seg.value);
      const payload = {
        id: 0,
        userId,
        createdBy: userId,
        segmentId,
        symbol: symName,
        script: symName,
        displayName: symName,
      };
      try {
        let expList = [];
        try {
          const a = await dispatch(getExpiryDatesBySymbol({ segmentId: seg.value, symbol: symName }));
          if (getExpiryDatesBySymbol.fulfilled.match(a) && Array.isArray(a.payload) && a.payload.length > 0)
            expList = a.payload;
        } catch {}
        if (!expList.length) {
          try {
            const r = await api.get(
              `/market-watch/get-expiry-dates-by-symbol/${seg.value}/${encodeURIComponent(symName)}`
            );
            const d = r?.data?.data ?? r?.data ?? [];
            if (Array.isArray(d)) expList = d;
          } catch {}
        }
        const dateStr = pickExpiry(expList);
        if (dateStr) {
          const [dd, mm, yyyy] = dateStr.split("-");
          payload.expiryDate = `${yyyy}-${mm}-${dd}`;
          payload.script = `${symName} ${buildScriptSuffix(dateStr)}`;
          const expiryObj = expList[0];
          payload.displayName =
            typeof expiryObj === "object"
              ? expiryObj?.value ?? expiryObj?.displayName ?? payload.script
              : payload.script;
        }
        // POST {REACT_APP_API_BASE}/api/v1/market-watch/save-market-watch
        const res = await api.post("market-watch/save-market-watch", payload);
        const body = res?.data ?? {};
        const httpOk = res?.status === 200 || res?.status === 201;
        const st = body.status;
        const explicitFail =
          Number(st) >= 400 ||
          st === false ||
          body.success === false ||
          body.Success === false;
        if (httpOk && !explicitFail) {
          await refreshMarketWatchList();
          const msg = body.message || body.Message;
          if (msg) dispatch(showMessage({ message: String(msg), varient: "success" }));
          if (!stayOnView) {
            setView("watchlist");
            setSearchText("");
            setGlobalScriptHits([]);
          }
        } else {
          const failMsg =
            body.message ||
            body.Message ||
            (explicitFail ? "Could not add symbol to watchlist" : "Request failed");
          dispatch(
            showMessage({
              message: String(failMsg),
              varient: /disabled|used\s*=\s*no/i.test(String(failMsg)) ? "warning" : undefined,
            })
          );
        }
      } catch (err) {
        const msg = getAxiosErrorMessage(err, "Could not add symbol to watchlist");
        dispatch(showMessage({
          message: String(msg),
          varient: /disabled|used\s*=\s*no/i.test(String(msg)) ? "warning" : undefined,
        }));
      } finally {
        setAddingSymbol(prev => { const n = new Set(prev); n.delete(symName); return n; });
      }
    },
    [dispatch, addingSymbol, refreshMarketWatchList]
  );

  const handleAdd = useCallback(
    (sym) => addSymbolForSegment(selectedSegment, sym, { stayOnView: true }),
    [addSymbolForSegment, selectedSegment]
  );

  useEffect(() => {
    if (view !== "segments" || searchText.trim().length < 2 || segments.length === 0) {
      setGlobalScriptHits([]);
      setGlobalSearchLoading(false);
      return;
    }
    const q = searchText.trim();
    const timer = setTimeout(async () => {
      const session = getSessionData();
      const userId = session.userId || session.UserId || session.id || 0;
      if (!userId) {
        setGlobalScriptHits([]);
        setGlobalSearchLoading(false);
        return;
      }
      setGlobalSearchLoading(true);
      const hits = [];
      const qLower = q.toLowerCase();

      const loadSegmentSymbolsForFallback = async (seg) => {
        const id = seg?.value;
        if (id == null || id === "") return [];
        const key = String(id);
        const cache = segmentSymbolsSearchCacheRef.current;
        if (cache.has(key)) return cache.get(key);
        let arr = [];
        try {
          const res = await api.get(`/market-watch/get-MW-symbols-for-nse/${id}`);
          if (res?.status === 200 && Array.isArray(res?.data?.data)) arr = res.data.data;
        } catch {
          /* same order as loadSymbols: NSE MW first */
        }
        if (!arr.length) {
          try {
            const res2 = await api.get(`/market-watch/get-symbols-by-segment/${id}`);
            const body = res2?.data;
            if (body?.status === 200 && Array.isArray(body?.data)) arr = body.data;
          } catch {
            /* ignore */
          }
        }
        cache.set(key, arr);
        setSegmentTotalsVersion((v) => v + 1);
        return arr;
      };

      try {
        await Promise.all(
          segments.map(async (seg) => {
            let names = new Set();
            try {
              const res = await api.get(
                `market-watch/get-all-script-by-user/${userId}/${seg.value}?Symbol=${encodeURIComponent(q)}`
              );
              const raw = normalizeMwSymbolArray(res?.data?.data ?? res?.data);
              names = collectSymbolNamesMatchingQuery(raw, qLower);
            } catch {
              /* futures / some segments may not support this route */
            }
            if (names.size === 0) {
              const full = await loadSegmentSymbolsForFallback(seg);
              names = collectSymbolNamesMatchingQuery(full, qLower);
            }
            names.forEach((name) => hits.push({ segment: seg, name }));
          })
        );
      } finally {
        setGlobalScriptHits(hits);
        setGlobalSearchLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText, view, segments]);

  useEffect(() => {
    if (!ctxMenu) return;
    const close = (ev) => {
      const t = ev.target;
      if (t && typeof t.closest === "function" && t.closest(".mw-ctx-menu")) return;
      setCtxMenu(null);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [ctxMenu]);

  useEffect(() => {
    return () => {
      if (bidAskTapTimerRef.current) {
        clearTimeout(bidAskTapTimerRef.current);
        bidAskTapTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!rowActionSheet) return;
    const close = (ev) => {
      const t = ev.target;
      if (t && typeof t.closest === "function" && t.closest(".mw-row-action-sheet")) return;
      setRowActionSheet(null);
    };
    const onKey = (ev) => {
      if (ev.key === "Escape") setRowActionSheet(null);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [rowActionSheet]);

  useEffect(() => {
    if (!specRow) return;
    const onKey = (ev) => {
      if (ev.key === "Escape") setSpecRow(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [specRow]);

  const handleRemoveByName = useCallback(async (symName) => {
    if (!symName) return;
    if (deletingSymbol.has(symName)) return;
    setDeletingSymbol(prev => { const n = new Set(prev); n.add(symName); return n; });
    try {
      const baseName = String(symName).split(/\s+/)[0];
      const match = watchRows.find((r) =>
        r?.symbol === baseName || r?.displayName === symName ||
        (typeof r?.script === "string" && (r.script === symName || r.script.startsWith(baseName + " "))) ||
        r?.name === baseName
      );
      if (match) {
        const mid = match.id ?? match.Id ?? match.marketWatchId;
        if (mid == null || mid === "") throw new Error("Missing watchlist entry id");
        await dispatch(
          deleteMarketWatch({ id: mid, displayName: match.displayName || match.symbol || String(mid) })
        ).unwrap();
        await refreshMarketWatchList();
      } else {
        dispatch(showMessage({ message: `Symbol "${symName}" not found in watchlist` }));
      }
    } catch (err) {
      dispatch(
        showMessage({
          message: String(err?.message || err || "Remove from watchlist failed"),
        })
      );
    } finally {
      setDeletingSymbol(prev => { const n = new Set(prev); n.delete(symName); return n; });
    }
  }, [dispatch, deletingSymbol, watchRows, refreshMarketWatchList]);

  const handleRemoveRow = useCallback(
    async (e, row) => {
      e?.stopPropagation?.();
      e?.preventDefault?.();
      const id = row?.id ?? row?.Id ?? row?.marketWatchId;
      const dn = row?.displayName || row?.script || row?.symbol || "";
      const symKey = dn || String(id);
      if (deletingSymbol.has(symKey)) return;
      try {
        if (id != null && id !== "") {
          setDeletingSymbol(prev => { const n = new Set(prev); n.add(symKey); return n; });
          await dispatch(deleteMarketWatch({ id, displayName: dn || String(id) })).unwrap();
          await refreshMarketWatchList();
        } else {
          const base = row?.symbol || row?.script || row?.displayName;
          if (base) await handleRemoveByName(String(base).split(/\s+/)[0]);
          else dispatch(showMessage({ message: "Cannot remove — missing symbol id" }));
        }
      } catch (err) {
        const msg =
          err?.message ||
          err?.response?.data?.message ||
          err?.response?.data?.Message ||
          "Remove from watchlist failed";
        dispatch(showMessage({ message: String(msg) }));
      } finally {
        setDeletingSymbol(prev => { const n = new Set(prev); n.delete(symKey); return n; });
      }
    },
    [dispatch, deletingSymbol, handleRemoveByName, refreshMarketWatchList]
  );

  const clearableWatchRows = useMemo(
    () =>
      watchRows
        .map((r) => {
          const id = r?.id ?? r?.Id;
          if (id == null || String(id).trim() === "") return null;
          return { ...r, id };
        })
        .filter(Boolean),
    [watchRows]
  );

  const handleClearAllWatchlist = useCallback(async () => {
    if (viewOnly || clearingAll || clearableWatchRows.length === 0) return;
    setClearingAll(true);
    try {
      await dispatch(multipleDeleteMarketWatch(clearableWatchRows)).unwrap();
      await refreshMarketWatchList();
      setClearWatchlistOpen(false);
      dispatch(
        showMessage({
          message: `Removed ${clearableWatchRows.length} symbol${clearableWatchRows.length === 1 ? "" : "s"} from watchlist`,
        })
      );
    } catch (err) {
      const msg =
        err?.message ||
        err?.response?.data?.message ||
        err?.response?.data?.Message ||
        "Could not clear watchlist";
      dispatch(showMessage({ message: String(msg) }));
    } finally {
      setClearingAll(false);
    }
  }, [viewOnly, clearingAll, clearableWatchRows, dispatch, refreshMarketWatchList]);

  useEffect(() => {
    if (!clearWatchlistOpen) return;
    const onKey = (ev) => {
      if (ev.key === "Escape" && !clearingAll) setClearWatchlistOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [clearWatchlistOpen, clearingAll]);

  const renderWatchlistRemoveHeader = () =>
    viewOnly ? (
      <div className="mw-wl-col mw-wl-col--remove mw-watchlist-col-remove" aria-hidden />
    ) : (
      <div className="mw-wl-col mw-wl-col--remove mw-watchlist-col-remove mw-watchlist-col-remove--action">
        <MwWatchlistClearAllHeaderButton
          disabled={clearableWatchRows.length === 0 || clearingAll}
          onClick={(e) => {
            e.stopPropagation();
            if (clearableWatchRows.length > 0) setClearWatchlistOpen(true);
          }}
        />
      </div>
    );

  const fmt = (v, d = 2) => {
    if (v == null || v === "") return "0.00";
    const n = Number(v);
    return isNaN(n) ? "0.00" : n.toFixed(d);
  };
  const fmtLive = (v, d = 2) => {
    const n = Number(v);
    if (v == null || v === "" || !Number.isFinite(n) || n <= 0) return "";
    return n.toFixed(d);
  };
  const fmtInt = (v) => {
    const n = Number(v);
    return isNaN(n) ? "0" : n.toLocaleString();
  };
  const openRowActionSheet = useCallback(
    (row, chartKey) => {
      if (!row) return;
      const fullLabel = formatWatchlistRowLabel(row);
      const symParts = splitWatchlistLabel(fullLabel);
      setPickedChartKey(chartKey);
      setRowActionSheet({
        row,
        chartKey,
        fullLabel,
        symParts,
      });
    },
    []
  );

  const openOrderFromBidAsk = useCallback(
    (row, chartKey) => {
      if (viewOnly || !onOpenQuickOrder) return;
      if (onSymbolSelectWithRow) onSymbolSelectWithRow(chartKey, row);
      else if (onSymbolSelect) onSymbolSelect(chartKey);
      onOpenQuickOrder(row);
    },
    [viewOnly, onOpenQuickOrder, onSymbolSelectWithRow, onSymbolSelect]
  );

  const onBidAskCellClick = useCallback(
    (e, row, chartKey) => {
      e.stopPropagation();
      if (!mobileFullscreen) {
        if (viewOnly || !onBidAskOpenOrder) return;
        if (onSymbolSelectWithRow) onSymbolSelectWithRow(chartKey, row);
        else if (onSymbolSelect) onSymbolSelect(chartKey);
        onBidAskOpenOrder(row);
        return;
      }
      const tapKey = String(chartKey);
      const now = Date.now();
      const last = bidAskTapRef.current;

      if (last.key === tapKey && now - last.t < 450) {
        if (bidAskTapTimerRef.current) {
          clearTimeout(bidAskTapTimerRef.current);
          bidAskTapTimerRef.current = null;
        }
        bidAskTapRef.current = { key: "", t: 0 };
        openOrderFromBidAsk(row, chartKey);
        return;
      }

      if (bidAskTapTimerRef.current) {
        clearTimeout(bidAskTapTimerRef.current);
        bidAskTapTimerRef.current = null;
      }

      bidAskTapRef.current = { key: tapKey, t: now };
      bidAskTapTimerRef.current = setTimeout(() => {
        bidAskTapTimerRef.current = null;
        bidAskTapRef.current = { key: "", t: 0 };
        openRowActionSheet(row, chartKey);
      }, 280);
    },
    [
      mobileFullscreen,
      viewOnly,
      onBidAskOpenOrder,
      onSymbolSelectWithRow,
      onSymbolSelect,
      openOrderFromBidAsk,
      openRowActionSheet,
    ]
  );

  const onBidAskCellDoubleClick = useCallback(
    (e, row, chartKey) => {
      e.stopPropagation();
      if (!mobileFullscreen) return;
      if (bidAskTapTimerRef.current) {
        clearTimeout(bidAskTapTimerRef.current);
        bidAskTapTimerRef.current = null;
      }
      bidAskTapRef.current = { key: "", t: 0 };
      openOrderFromBidAsk(row, chartKey);
    },
    [mobileFullscreen, openOrderFromBidAsk]
  );

  const specSections = useMemo(
    () => (specRow ? buildWatchlistSpecSections(specRow, t) : []),
    [specRow, t]
  );
  const specBodyRef = useRef(null);
  useEffect(() => {
    if (!specRow) return;
    const el = specBodyRef.current;
    if (el) el.scrollTop = 0;
  }, [specRow]);

  const canReorderWatchlist = !viewOnly && !searchText.trim() && view === "watchlist";

  const moveRowOrder = useCallback(
    (fromIdx, toIdx) => {
      const arr = [...watchRows];
      if (
        fromIdx < 0 ||
        toIdx < 0 ||
        fromIdx >= arr.length ||
        toIdx >= arr.length ||
        fromIdx === toIdx
      )
        return;
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      try {
        const session = getSessionData();
        const uid = session.userId || session.UserId || session.id;
        if (uid) {
          localStorage.setItem(
            `fxbazzar_mw_order_${uid}`,
            JSON.stringify(arr.map((r) => r.id).filter((x) => x != null))
          );
        }
      } catch {
        /* ignore */
      }
      dispatch(reorderMarketWatchListOrder(arr));
    },
    [watchRows, dispatch]
  );

  useEffect(() => {
    moveRowOrderRef.current = moveRowOrder;
  }, [moveRowOrder]);

  const watchlistPanelRef = useRef(null);
  const [wlDensity, setWlDensity] = useState("full"); // full | compact | mini — panel width

  useEffect(() => {
    if (mobileFullscreen) {
      setWlDensity("full");
      return undefined;
    }
    const el = watchlistPanelRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const compactThreshold = Math.max(260, (Number(watchlistMinWidthPx) || 358) - 62);
    let rafId = null;
    const ro = new ResizeObserver(() => {
      if (rafId != null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (!el.isConnected) return;
        const w = el.getBoundingClientRect().width;
        if (w < 248) setWlDensity("mini");
        else if (w < compactThreshold) setWlDensity("compact");
        else setWlDensity("full");
      });
    });
    ro.observe(el);
    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [watchlistMinWidthPx, mobileFullscreen]);

  const effectiveWlDensity = mobileFullscreen ? "mini" : wlDensity;
  const dragColW = 26;
  const delColW = 36;
  // Always show Bid / Ask / LTP in collapsed mode; narrow panels scroll horizontally.
  const showBidAskCols = true;
  const miniWl = effectiveWlDensity === "mini" || mobileFullscreen;
  const mobileWl = mobileFullscreen;
  const dragW = mobileWl ? 14 : miniWl ? 22 : dragColW;
  const delW = mobileWl ? 38 : miniWl ? 30 : delColW;
  // MetaTrader Market Watch — desktop matches MT5 density; mobile keeps larger touch targets.
  const wlColGap = mt5Ui ? (mobileWl ? 4 : 10) : mobileWl ? 6 : miniWl ? 4 : isExpanded ? 4 : 6;
  const wlPadRow = mt5Ui
    ? mobileWl
      ? "3px 0"
      : "10px 8px"
    : mobileWl
      ? "8px 10px"
      : miniWl
        ? "3px 6px"
        : "2px 8px";
  const wlPadHead = mt5Ui
    ? mobileWl
      ? "4px 4px"
      : "5px 8px"
    : mobileWl
      ? "8px 10px"
      : miniWl
        ? "4px 8px"
        : "3px 8px";
  const wlFontHead = mt5Ui ? (mobileWl ? 13 : 13) : mobileWl ? 10 : miniWl ? 10 : 11;
  const wlFontRow = mt5Ui ? (mobileWl ? 13 : 14) : mobileWl ? 13 : miniWl ? 11 : 12;
  const mobileRowMinH = mt5Ui ? (mobileWl ? 40 : 40) : mobileWl ? 46 : 34;

  // MT5: Symbol + Bid/Ask/LTP (mobile Quotes last column is LTP)
  // Legacy: grip | symbol | bid | ask | ltp | delete
  const mt5SymColPx = mobileWl ? 118 : 148;
  const mt5MobileQuoteCols = false;
  const gridColsMt5Collapsed = mobileWl
    ? mt5MobileQuoteCols
      ? "minmax(0, 1.55fr) minmax(0, 0.92fr) minmax(0, 0.92fr) minmax(0, 0.88fr)"
      : "minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)"
    : `minmax(${mt5SymColPx}px, 1.4fr) minmax(92px, 1fr) minmax(92px, 1fr) minmax(124px, 1.15fr)`;
  const gridColsCollapsedFull = `${dragW}px minmax(0, 2fr) repeat(3, minmax(0, 1fr)) ${delW}px`;
  const gridColsCollapsedCompact = `${dragW}px minmax(0, 1.55fr) minmax(0, 1fr) ${delW}px`;
  const gridColsCollapsedMini = `${dragW}px minmax(0, 1fr) minmax(0, 0.95fr) ${delW}px`;
  const gridColsCollapsed = mt5CollapsedQuotes
    ? gridColsMt5Collapsed
    : effectiveWlDensity === "mini"
      ? gridColsCollapsedMini
      : effectiveWlDensity === "compact"
        ? gridColsCollapsedCompact
        : gridColsCollapsedFull;
  const expandedGap = 6;
  const expandedSymbolMin = 200;
  const expandedCellMin = 78;
  const expandedNumCols = 10;
  const expandedGaps = expandedNumCols;
  const expandedPadX = 28;
  const expandedMinOuterPx =
    dragColW +
    expandedSymbolMin +
    expandedNumCols * expandedCellMin +
    expandedGaps * expandedGap +
    expandedPadX +
    delColW;
  const gridColsExpanded = `${dragColW}px minmax(${expandedSymbolMin}px, 1.55fr) repeat(${expandedNumCols}, minmax(${expandedCellMin}px, 1fr)) ${delColW}px`;

  /**
   * MT5 collapsed: pinned Symbol | scrollable Bid/Ask/LTP (same as before).
   * Vertical line after Symbol stays; prices scroll in the tail pane.
   * Full-width H scrollbar is a visual track synced to the price pane.
   */
  // Mobile quotes fit Symbol | Bid | Ask | LTP on screen. Desktop keeps the pinned split.
  const mobileOptionalSplitMetrics =
    mobileFullscreen
      ? null
      : mt5CollapsedQuotes
        ? mwMobileOptionalSplitMetrics(0, 0, wlColGap, 0, wlFontRow, true)
        : null;
  const mobileMt5Quotes = mt5CollapsedQuotes;
  /**
   * Collapsed: fit columns to panel width (no forced min scroll width).
   * Expanded: sticky leading columns + horizontal scroll for extra cols.
   */
  const freezeSymbolForHorizontalScroll = !mobileFullscreen && isExpanded;
  const freezeMobileBidAsk = false;
  const fullScrollMetrics = null;
  const watchlistGridCols =
    isExpanded && !mobileFullscreen
      ? gridColsExpanded
      : gridColsCollapsed;
  // MT5 mins: Symbol + Bid + Ask + LTP so narrow panels scroll prices only
  const mt5CollapsedMinOuterPx = mobileWl
    ? mt5SymColPx + 78 + 78 + 78 + wlColGap * 3 + 20
    : mt5SymColPx + 92 + 92 + 124 + wlColGap * 3 + 28;
  const watchlistGridMinOuterPx =
    mobileFullscreen
      ? 0
      : isExpanded && !mobileFullscreen
        ? expandedMinOuterPx
        : mt5CollapsedQuotes
          ? mt5CollapsedMinOuterPx
          : 0;

  const useMobileSplitLayout = Boolean(mobileOptionalSplitMetrics?.useSplit);
  const mobilePinnedGridCols = mobileOptionalSplitMetrics?.pinnedGridCols || "";
  const mobileTailGridCols = mobileOptionalSplitMetrics?.tailGridCols || "";
  const mobileTailMinPx = mobileOptionalSplitMetrics?.tailMinPx || 0;
  const symbolColFitPx = useMemo(() => {
    const labels = filteredWatchRows.map((r) => formatWatchlistRowLabel(r));
    return measureWatchlistSymbolColPx(labels, wlFontRow);
  }, [filteredWatchRows, wlFontRow]);
  const mobilePinnedWidthPx = useMobileSplitLayout
    ? Math.max(64, symbolColFitPx)
    : mobileOptionalSplitMetrics?.pinnedWidthPx || 0;

  // MT5: pin Symbol only (no grip). Expanded: pin grip + Symbol.
  const freezeMt5SymbolOnly = mt5CollapsedQuotes && !mobileFullscreen;
  const freezeGripSymbol = mobileFullscreen
    ? false
    : useMobileSplitLayout
      ? false
      : freezeSymbolForHorizontalScroll || isExpanded || freezeMt5SymbolOnly;
  const stickyDragW = freezeMt5SymbolOnly ? 0 : isExpanded ? dragColW : dragW;
  const stickyGapW = freezeMt5SymbolOnly ? 0 : isExpanded ? expandedGap : wlColGap;

  const numCellWrap = {
    minWidth: 0,
    overflow: mobileFullscreen ? "visible" : "hidden",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  };
  const numCellEllipsis = {
    display: "block",
    overflow: mobileFullscreen ? "visible" : "hidden",
    textOverflow: mobileFullscreen ? "clip" : "ellipsis",
    whiteSpace: "nowrap",
    textAlign: "right",
  };

  const ui = darkTheme
    ? {
        panelBg: DARK_UI.bg,
        panelText: DARK_UI.text,
        mutedText: DARK_UI.muted,
        border: DARK_UI.border,
        cardBg: DARK_UI.bg,
        rowBg: DARK_UI.bg,
        rowSelectedBg: DARK_UI.rowSelected,
        inputBg: DARK_UI.inputBg,
        inputText: DARK_UI.text,
        headerBg: DARK_UI.bg,
        headerText: mobileFullscreen ? "#ffffff" : "rgba(255, 255, 255, 0.7)",
        buttonBg: DARK_UI.buttonBg,
        buttonBorder: DARK_UI.border,
      }
    : {
        panelBg: "#ffffff",
        panelText: "#1a1a1a",
        mutedText: "#6b7280",
        border: "#e6e6e6",
        cardBg: "#ffffff",
        rowBg: "#ffffff",
        rowSelectedBg: "#f3f8fc",
        inputBg: "#fff",
        inputText: "#1a1a1a",
        headerBg: "#ffffff",
        headerText: "#000000",
        buttonBg: "#ffffff",
        buttonBorder: "#c8c8c8",
      };

  return (
    <div
      className={`marketwatch marketwatch--mt5${darkTheme ? " marketwatch--dark" : " marketwatch--light"}${viewOnly ? " marketwatch--view-only" : ""}${mobileFullscreen ? " marketwatch--mobile-full" : ""}`}
      style={{
        userSelect: "none",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        position: mobileFullscreen ? "relative" : undefined,
        background: ui.panelBg,
        color: ui.panelText,
        padding: mobileFullscreen ? "0" : "0",
        gap: mobileFullscreen ? 0 : 0,
        ...(mobileFullscreen
          ? {
              ["--mw-wl-header-bg"]: darkTheme ? DARK_UI.bg : "#ffffff",
              ["--mw-wl-header-border"]: darkTheme
                ? DARK_UI.border
                : "#c8c8c8",
              ["--mw-wl-row-bg"]: darkTheme ? DARK_UI.bg : ui.rowBg,
              ["--mw-wl-row-selected"]: ui.rowSelectedBg,
            }
          : {}),
      }}
    >

      {/* Quotes search stays on the list (MT5). Typing or focusing it opens the symbol finder. */}
      <div
        className={`search-bar mw-search-bar mw-search-bar--mt5 mw-search-bar--mt5-only${
          mobileFullscreen ? " mw-search-bar--mobile-mt5" : ""
        }`}
        style={{ flexShrink: 0 }}
      >
        <div className="mw-search-bar__field">
          <span className="mw-search-bar__icon" aria-hidden>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
              <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </span>
          <input
            className="mw-search-bar__input"
            placeholder={t("searchSymbol")}
            value={searchText}
            onChange={(e) => {
              const next = e.target.value;
              setSearchText(next);
              if (view === "watchlist") {
                setView("segments");
                loadSegments();
              }
            }}
            onFocus={() => {
              if (view === "watchlist") {
                setGlobalScriptHits([]);
                setView("segments");
                loadSegments();
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                closeAddSymbol();
              }
            }}
            autoFocus={mobileFullscreen && browseMode}
          />
            {(searchText || view !== "watchlist") && (
              <button
                type="button"
                className="mw-search-bar__clear"
                aria-label={view === "watchlist" ? "Clear search" : "Close symbol search"}
                title={view === "watchlist" ? "Clear" : "Close"}
                onClick={closeAddSymbol}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            )}
        </div>
      </div>

      {/* WATCHLIST */}
      {view === "watchlist" && (
        <div
          ref={watchlistPanelRef}
          className="mw-watchlist-panel"
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            border: "none",
            borderRadius: 0,
            overflow: "hidden",
            background: ui.cardBg,
            position: "relative",
          }}
        >
          {viewOnly && (
            <div className="mw-view-only-ribbon" aria-hidden>
              View only
            </div>
          )}
          {/* Header pinned outside scroll — stays clean when rows scroll */}
          {/* MT5 collapsed header is inside the scroll area (one bottom scrollbar). */}
          {!useMobileSplitLayout && !(isExpanded && !mobileFullscreen) && !mt5CollapsedQuotes && (
            <div className={mobileFullscreen ? "mw-mobile-wl-fixed-header" : "mw-wl-fixed-header"}>
              <div
                className={`mw-watchlist-header-row${mobileFullscreen ? " mw-watchlist-header-row--mobile-fixed" : ""}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: watchlistGridCols,
                  boxSizing: "border-box",
                  minWidth: 0,
                  width: "100%",
                  columnGap: wlColGap,
                  fontWeight: 600,
                  padding: wlPadHead,
                  fontSize: wlFontHead,
                  flexShrink: 0,
                  background: ui.headerBg,
                  color: ui.headerText,
                  textTransform: "none",
                  letterSpacing: 0,
                }}
              >
                <div className="mw-wl-col mw-wl-col--grip" title="Drag to reorder" aria-hidden>
                  <span className="mw-wl-grip-icon">⋮⋮</span>
                </div>
                {renderMwColHead("symbol", "Symbol", t("symbol"), {
                  className: "mw-wl-col mw-wl-col--symbol",
                })}
                {renderMwColHead("bid", "Bid", t("bid"), {
                  className: "mw-wl-col mw-wl-col--price",
                  alignEnd: true,
                })}
                {renderMwColHead("ask", "Ask", t("ask"), {
                  className: "mw-wl-col mw-wl-col--price",
                  alignEnd: true,
                })}
                {renderMwColHead("ltp", "LTP", t("ltp"), {
                  className: "mw-wl-col mw-wl-col--price",
                  alignEnd: true,
                })}
                {renderWatchlistRemoveHeader()}
              </div>
            </div>
          )}
          <div
            ref={tableScrollRef}
            className={[
              "mw-watchlist-scroll",
              dragSourceIdx != null && canReorderWatchlist ? "mw-watchlist-scroll--dnd-active" : "",
              freezeGripSymbol && !useMobileSplitLayout ? "mw-watchlist-scroll--sticky-symbol" : "",
              freezeMt5SymbolOnly && !useMobileSplitLayout ? "mw-watchlist-scroll--mt5-sym-only" : "",
              useMobileSplitLayout ? "mw-watchlist-scroll--mobile-split" : "",
              useMobileSplitLayout && mobileMt5Quotes ? "mw-watchlist-scroll--mt5-split" : "",
              useMobileSplitLayout && mobileMt5Quotes ? "mw-watchlist-scroll--mt5-full-hbar" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            // MT5 split: scroll axes live in the price pane (H under Bid/Ask/LTP only).
            style={{
              overflowX: mobileFullscreen
                ? "hidden"
                : useMobileSplitLayout
                  ? "hidden"
                  : freezeGripSymbol || mt5CollapsedQuotes
                    ? "auto"
                    : "hidden",
              overflowY: useMobileSplitLayout ? "hidden" : "auto",
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              ...(useMobileSplitLayout || freezeGripSymbol
                ? {
                    ["--mw-wl-drag-w"]: `${stickyDragW}px`,
                    ["--mw-wl-col-gap"]: `${
                      useMobileSplitLayout || freezeMt5SymbolOnly
                        ? wlColGap
                        : stickyGapW
                    }px`,
                    ["--mw-wl-sym-w"]: `${
                      useMobileSplitLayout ? mobilePinnedWidthPx : mt5SymColPx
                    }px`,
                    ["--mw-wl-row-bg"]: darkTheme ? DARK_UI.bg : ui.rowBg,
                    ["--mw-wl-header-bg"]: darkTheme ? DARK_UI.bg : "#ffffff",
                    ["--mw-wl-pinned-w"]: useMobileSplitLayout
                      ? `${mobilePinnedWidthPx}px`
                      : undefined,
                    ["--mw-wl-tail-min"]: useMobileSplitLayout
                      ? `${mobileTailMinPx}px`
                      : undefined,
                  }
                : {}),
            }}
          >
            {mt5CollapsedQuotes && !useMobileSplitLayout && (
              <div
                className="mw-watchlist-header-row mw-watchlist-header-row--mt5-in-scroll"
                style={{
                  display: "grid",
                  gridTemplateColumns: watchlistGridCols,
                  boxSizing: "border-box",
                  minWidth: watchlistGridMinOuterPx,
                  width: "100%",
                  columnGap: wlColGap,
                  fontWeight: 600,
                  padding: wlPadHead,
                  fontSize: wlFontHead,
                  flexShrink: 0,
                  background: ui.headerBg,
                  color: ui.headerText,
                  textTransform: "none",
                  letterSpacing: 0,
                  position: "sticky",
                  top: 0,
                  zIndex: 12,
                }}
              >
                {renderMwColHead("symbol", t("symbol"), t("symbol"), {
                  className: "mw-wl-col mw-wl-col--symbol mw-wl-sticky mw-wl-sticky--sym",
                  style: { background: darkTheme ? DARK_UI.bg : "#ffffff" },
                })}
                {renderMwColHead("bid", t("bid"), t("bid"), {
                  className: "mw-wl-col mw-wl-col--price",
                  alignEnd: true,
                })}
                {renderMwColHead("ask", t("ask"), t("ask"), {
                  className: "mw-wl-col mw-wl-col--price",
                  alignEnd: true,
                })}
                {mt5MobileQuoteCols
                  ? renderMwColHead("chgPct", t("dailyChange"), t("dailyChange"), {
                      className: "mw-wl-col mw-wl-col--price mw-wl-col--chg",
                      alignEnd: true,
                    })
                  : renderMwColHead("ltp", t("ltp"), t("ltp"), {
                      className: "mw-wl-col mw-wl-col--price",
                      alignEnd: true,
                    })}
              </div>
            )}
            {useMobileSplitLayout && (
              <div className={`mw-mobile-wl-split${mobileMt5Quotes ? " mw-mobile-wl-split--mt5" : ""}`}>
                <div className="mw-mobile-wl-sticky-header">
                  <div
                    className="mw-mobile-wl-pinned mw-mobile-wl-pinned--header"
                    style={{ width: mobilePinnedWidthPx, flexShrink: 0 }}
                  >
                    <div
                      className="mw-watchlist-header-row mw-mobile-wl-line"
                      style={{
                        display: "grid",
                        gridTemplateColumns: mobilePinnedGridCols,
                        columnGap: wlColGap,
                        padding: wlPadHead,
                        fontWeight: 700,
                        fontSize: wlFontHead,
                        color: ui.headerText,
                        textTransform: mobileMt5Quotes ? "none" : "uppercase",
                      }}
                    >
                      {!mobileMt5Quotes && (
                        <div style={{ textAlign: "center", color: ui.mutedText, fontSize: 11 }}>≡</div>
                      )}
                      {renderMwColHead("symbol", t("symbol"), t("symbol"), {
                        className: "mw-wl-col mw-wl-col--symbol",
                        style: { textAlign: "left" },
                      })}
                    </div>
                  </div>
                  <div
                    className="mw-mobile-wl-tail-scroll mw-mobile-wl-tail-scroll--header"
                    ref={mobileTailHeadScrollRef}
                    onScroll={syncMobileTailScrollFromHead}
                  >
                    <div
                      className="mw-mobile-wl-tail-inner"
                      style={{ minWidth: mobileTailMinPx, width: "100%" }}
                    >
                      <div
                        className="mw-watchlist-header-row mw-mobile-wl-line"
                        style={{
                          display: "grid",
                          gridTemplateColumns: mobileTailGridCols,
                          columnGap: wlColGap,
                          padding: wlPadHead,
                          fontWeight: 700,
                          fontSize: wlFontHead,
                          color: ui.headerText,
                          textTransform: mobileMt5Quotes ? "none" : "uppercase",
                          width: "100%",
                        }}
                      >
                        {renderMwColHead("bid", t("bid"), t("bid"), {
                          className: "mw-wl-col mw-wl-col--price",
                          style: { textAlign: "right" },
                          alignEnd: true,
                        })}
                        {renderMwColHead("ask", t("ask"), t("ask"), {
                          className: "mw-wl-col mw-wl-col--price",
                          style: { textAlign: "right" },
                          alignEnd: true,
                        })}
                        {mt5MobileQuoteCols
                          ? renderMwColHead("chgPct", t("dailyChange"), t("dailyChange"), {
                              className: "mw-wl-col mw-wl-col--price mw-wl-col--chg",
                              style: { textAlign: "right" },
                              alignEnd: true,
                            })
                          : renderMwColHead("ltp", t("ltp"), t("ltp"), {
                              className: "mw-wl-col mw-wl-col--price",
                              style: { textAlign: "right" },
                              alignEnd: true,
                            })}
                        {mobileHasOptionalCols &&
                          activeOptionalColDefs.map((col) => (
                            <React.Fragment key={col.id}>
                              {renderMwColHead(
                                col.id,
                                col.id === "chgPct" ? t("dailyChange") : col.label,
                                col.title,
                                { style: { textAlign: "right" }, alignEnd: true }
                              )}
                            </React.Fragment>
                          ))}
                        {!mobileMt5Quotes && renderWatchlistRemoveHeader()}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mw-mobile-wl-split-body">
                  <div
                    className="mw-mobile-wl-pinned mw-mobile-wl-pinned--body"
                    ref={mobilePinnedBodyScrollRef}
                    onScroll={syncMobilePinnedBodyScroll}
                    style={{ width: mobilePinnedWidthPx, flexShrink: 0 }}
                  >
                    {sortedWatchRows.map((row, fIdx) => {
                      const fullLabel = formatWatchlistRowLabel(row);
                      const chartKey = row?.script ?? row?.symbol ?? row?.displayName ?? fullLabel;
                      const isSelected = mobileFullscreen
                        ? pickedChartKey === chartKey
                        : (selectedChartSymbol || "") === chartKey;
                      const rowBg = isSelected
                        ? mobileMt5Quotes
                          ? undefined
                          : ui.rowSelectedBg
                        : ui.rowBg;
                      const handleSelect = () => {
                        if (onSymbolSelectWithRow) onSymbolSelectWithRow(chartKey, row);
                        else if (onSymbolSelect) onSymbolSelect(chartKey);
                      };
                      const handleRowTap = () => {
                        if (mobileFullscreen) openRowActionSheet(row, chartKey);
                        else handleSelect();
                      };
                      return (
                        <div
                          key={`p-${row?.id ?? fullLabel}-${fIdx}`}
                          className={[
                            "mw-watchlist-row",
                            "mw-mobile-wl-line",
                            isSelected ? "mw-watchlist-row--selected" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          style={{
                            display: "grid",
                            gridTemplateColumns: mobilePinnedGridCols,
                            columnGap: wlColGap,
                            padding: wlPadRow,
                            fontSize: wlFontRow,
                            background: rowBg,
                            borderBottom: "none",
                            alignItems: "center",
                            minHeight: mobileRowMinH,
                          }}
                          onClick={handleRowTap}
                          role="button"
                          tabIndex={0}
                          title={
                            String(row?.description ?? row?.Description ?? "").trim() || undefined
                          }
                        >
                          {!mobileMt5Quotes && (
                            <div style={{ textAlign: "center", color: ui.mutedText, fontSize: 11 }}>≡</div>
                          )}
                          <div className="mw-mt5-symbol-cell" style={{ minWidth: 0, textAlign: "left" }}>
                            <MwMt5DirIcon dir={mwMt5DirFromRow(row)} />
                            {mobileFullscreen ? (
                              <MobileSymbolLabel fullLabel={fullLabel} />
                            ) : (
                              <span className="mw-mt5-symbol-text" title={fullLabel}>
                                <span className="mw-symbol-label">{fullLabel}</span>
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div
                    className="mw-mobile-wl-tail-scroll mw-mobile-wl-tail-scroll--body"
                    ref={mobileTailBodyScrollRef}
                    onScroll={syncMobileTailScrollFromBody}
                  >
                    <div
                      className="mw-mobile-wl-tail-inner"
                      style={{ minWidth: mobileTailMinPx, width: "100%" }}
                    >
                      {sortedWatchRows.map((row, fIdx) => {
                        const fullLabel = formatWatchlistRowLabel(row);
                        const bid = row?.buyPrice ?? row?.bid ?? 0;
                        const ask = row?.sellPrice ?? row?.ask ?? 0;
                        const ltp = row?.lastTradedPrice ?? row?.ltp ?? 0;
                        const chartKey = row?.script ?? row?.symbol ?? row?.displayName ?? fullLabel;
                        const isSelected = mobileFullscreen
                          ? pickedChartKey === chartKey
                          : (selectedChartSymbol || "") === chartKey;
                        const rowBg = isSelected
                          ? mobileMt5Quotes
                            ? undefined
                            : ui.rowSelectedBg
                          : ui.rowBg;
                        const colorBid = mwMt5PriceColor(row?.colorBid, row, ui.panelText);
                        const colorAsk = mwMt5PriceColor(row?.colorAsk, row, ui.panelText);
                        const colorLTP = mwMt5PriceColor(row?.colorLTP, row, ui.panelText);
                        const handleSelect = () => {
                          if (onSymbolSelectWithRow) onSymbolSelectWithRow(chartKey, row);
                          else if (onSymbolSelect) onSymbolSelect(chartKey);
                        };
                        const handleRowTap = () => {
                          if (mobileFullscreen) openRowActionSheet(row, chartKey);
                          else handleSelect();
                        };
                        const openBidAsk = (e) => onBidAskCellClick(e, row, chartKey);
                        const openBidAskDbl = (e) => onBidAskCellDoubleClick(e, row, chartKey);
                        return (
                          <div
                            key={`t-${row?.id ?? fullLabel}-${fIdx}`}
                            className={`mw-watchlist-row mw-mobile-wl-line${isSelected ? " mw-watchlist-row--selected" : ""}`}
                            style={{
                              display: "grid",
                              gridTemplateColumns: mobileTailGridCols,
                              columnGap: wlColGap,
                              padding: wlPadRow,
                              fontSize: wlFontRow,
                              background: rowBg,
                              width: "100%",
                              borderBottom: "none",
                              alignItems: "center",
                              minHeight: mobileRowMinH,
                              boxSizing: "border-box",
                            }}
                            onClick={handleRowTap}
                            role="button"
                            tabIndex={0}
                          >
                            <div
                              className="mw-wl-col mw-wl-col--price"
                              style={{ ...numCellWrap, textAlign: "right" }}
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation();
                                openBidAsk(e);
                              }}
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                openBidAskDbl(e);
                              }}
                              onKeyDown={(e) => e.key === "Enter" && openBidAsk(e)}
                            >
                              <span
                                className="mw-mt5-tick"
                                style={{ ...numCellEllipsis, color: colorBid || ui.panelText, fontWeight: 400 }}
                              >
                                {fmtLive(bid)}
                              </span>
                            </div>
                            <div
                              className="mw-wl-col mw-wl-col--price"
                              style={{ ...numCellWrap, textAlign: "right" }}
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation();
                                openBidAsk(e);
                              }}
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                openBidAskDbl(e);
                              }}
                              onKeyDown={(e) => e.key === "Enter" && openBidAsk(e)}
                            >
                              <span
                                className="mw-mt5-tick"
                                style={{ ...numCellEllipsis, color: colorAsk || ui.panelText, fontWeight: 400 }}
                              >
                                {fmtLive(ask)}
                              </span>
                            </div>
                            <div className="mw-wl-col mw-wl-col--price" style={{ ...numCellWrap, textAlign: "right" }}>
                              <span
                                className="mw-mt5-tick"
                                style={{ ...numCellEllipsis, color: colorLTP || ui.panelText, fontWeight: 400 }}
                              >
                                {fmtLive(ltp)}
                              </span>
                            </div>
                            {mobileHasOptionalCols
                              ? renderMwOptionalColCells(activeOptionalColDefs, row, ui, fmt, fmtInt)
                              : null}
                            {!mobileMt5Quotes && (
                              <div className="mw-wl-col mw-wl-col--remove mw-watchlist-remove-cell" onClick={(e) => e.stopPropagation()}>
                                {!viewOnly && (
                                  <MwWatchlistRemoveButton
                                    busy={deletingSymbol.has(
                                      (row?.displayName || row?.script || row?.symbol || "") ||
                                        String(row?.id ?? row?.Id ?? "")
                                    )}
                                    onClick={(e) => handleRemoveRow(e, row)}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                {mobileMt5Quotes ? (
                  <div
                    className="mw-mt5-hscroll"
                    ref={mt5FullHScrollRef}
                    onScroll={syncMt5FullHScroll}
                    aria-hidden
                  >
                    <div
                      className="mw-mt5-hscroll__spacer"
                      style={{
                        width: mobilePinnedWidthPx + mobileTailMinPx,
                        height: 1,
                      }}
                    />
                  </div>
                ) : null}
              </div>
            )}
            {!useMobileSplitLayout && (
            <>
            {isExpanded && !mobileFullscreen && (
            <div
              className="mw-watchlist-header-row"
              style={{
                display: "grid",
                gridTemplateColumns: watchlistGridCols,
                boxSizing: "border-box",
                minWidth: watchlistGridMinOuterPx,
                width: "100%",
                columnGap: isExpanded && !mobileFullscreen ? expandedGap : wlColGap,
                fontWeight: 600,
                padding: wlPadHead,
                borderBottom: mt5Ui ? "none" : `1px solid ${ui.border}`,
                fontSize: wlFontHead,
                flexShrink: 0,
                background: ui.headerBg,
                color: ui.headerText,
                textTransform: "none",
                letterSpacing: 0,
                position: "sticky",
                top: 0,
                zIndex: freezeGripSymbol ? 25 : 12,
              }}
            >
              <div
                className={freezeGripSymbol ? "mw-wl-sticky mw-wl-sticky--grip" : undefined}
                style={{ minWidth: 0, textAlign: "center", fontSize: miniWl ? 9 : 10, color: ui.mutedText }}
                title="Drag to reorder"
              >
                ≡
              </div>
              {renderMwColHead("symbol", t("symbol"), t("symbol"), {
                className: freezeGripSymbol ? "mw-wl-sticky mw-wl-sticky--sym" : undefined,
                style: { minWidth: 0, textAlign: "left" },
              })}
              {showBidAskCols && (
                <>
                  {renderMwColHead("bid", t("bid"), t("bid"), {
                    className: freezeMobileBidAsk ? "mw-wl-sticky mw-wl-sticky--bid" : undefined,
                    style: { minWidth: 0, textAlign: "right", fontVariantNumeric: "tabular-nums" },
                    alignEnd: true,
                  })}
                  {renderMwColHead("ask", t("ask"), t("ask"), {
                    className: freezeMobileBidAsk ? "mw-wl-sticky mw-wl-sticky--ask" : undefined,
                    style: { minWidth: 0, textAlign: "right", fontVariantNumeric: "tabular-nums" },
                    alignEnd: true,
                  })}
                </>
              )}
              {mt5CollapsedQuotes ? (
                mt5MobileQuoteCols ? (
                  renderMwColHead("chgPct", t("dailyChange"), t("dailyChange"), {
                    className: "mw-wl-col--chg",
                    style: { minWidth: 0, textAlign: "right", fontVariantNumeric: "tabular-nums" },
                    alignEnd: true,
                  })
                ) : (
                  renderMwColHead("ltp", t("ltp"), t("ltp"), {
                    style: { minWidth: 0, textAlign: "right", fontVariantNumeric: "tabular-nums" },
                    alignEnd: true,
                  })
                )
              ) : (
                <>
                  {renderMwColHead("chgPct", t("dailyChange"), t("dailyChange"), {
                    style: { minWidth: 0, textAlign: "right", fontVariantNumeric: "tabular-nums" },
                    alignEnd: true,
                  })}
                  {renderMwColHead("ltp", t("ltp"), t("ltp"), {
                    style: { minWidth: 0, textAlign: "right", fontVariantNumeric: "tabular-nums" },
                    alignEnd: true,
                  })}
                </>
              )}
              {showOptionalCols &&
                activeOptionalColDefs.map((col) => (
                  <React.Fragment key={col.id}>
                    {renderMwColHead(col.id, col.label, col.title, {
                      style: { minWidth: 0, textAlign: "right", fontVariantNumeric: "tabular-nums" },
                      alignEnd: true,
                    })}
                  </React.Fragment>
                ))}
              {!mt5CollapsedQuotes && renderWatchlistRemoveHeader()}
            </div>
            )}
            <div
              className="mw-watchlist-rows"
              style={{
                boxSizing: "border-box",
                minWidth: watchlistGridMinOuterPx,
                width: "100%",
              }}
            >
            {marketWatchLoading && watchRows.length === 0 && <div style={{ padding: 12 }}>{t("loading")}</div>}
            {!marketWatchLoading && watchRows.length === 0 && <div style={{ padding: 12, color: ui.mutedText }}>{t("noSymbols")}</div>}
            {sortedWatchRows.map((row, fIdx) => {
              const stableId = row?.id ?? row?.Id;
              const rowSig = `${row?.displayName ?? ""}|${row?.script ?? ""}|${row?.symbol ?? ""}`;
              const idx = canReorderWatchlist
                ? stableId != null && stableId !== ""
                  ? watchRows.findIndex((r) => r && String(r?.id ?? r?.Id) === String(stableId))
                  : watchRows.findIndex(
                      (r) =>
                        r &&
                        `${r?.displayName ?? ""}|${r?.script ?? ""}|${r?.symbol ?? ""}` === rowSig
                    )
                : fIdx;
              const rowDragOk = canReorderWatchlist && watchRows.length > 1 && idx >= 0;
              const fullLabel = formatWatchlistRowLabel(row);
              const symParts = mobileFullscreen ? splitWatchlistLabel(fullLabel) : null;
              const displayLabel = fullLabel;
              const bid      = row?.buyPrice        ?? row?.bid  ?? 0;
              const ask      = row?.sellPrice       ?? row?.ask  ?? 0;
              const ltp      = row?.lastTradedPrice ?? row?.ltp  ?? 0;
              const chgPct   = row?.changePercent   ?? 0;
              const chgColor = row?.changeAmountColor ?? "";
              const colorBid = mwMt5PriceColor(row?.colorBid, row, ui.panelText);
              const colorAsk = mwMt5PriceColor(row?.colorAsk, row, ui.panelText);
              const colorLTP = mwMt5PriceColor(row?.colorLTP, row, ui.panelText);
              const open = row?.open ?? 0; const high = row?.high ?? 0;
              const low  = row?.low  ?? 0; const prev = row?.close ?? row?.previousClose ?? 0;
              const vol  = row?.volume ?? 0;
              const chartKey = row?.script ?? row?.symbol ?? row?.displayName ?? displayLabel;

              let rowClass = "";
              if (high > 0 && ltp > 0) {
                if (high === ltp) rowClass = "today-high";
                else if (low === ltp) rowClass = "today-low";
              }

              const isSelected = mobileFullscreen
                ? pickedChartKey === chartKey
                : (selectedChartSymbol || "") === chartKey;
              const rowBg = isSelected
                ? mt5Ui
                  ? undefined
                  : ui.rowSelectedBg
                : ui.rowBg;
              const handleSelect = () => {
                if (onSymbolSelectWithRow) onSymbolSelectWithRow(chartKey, row);
                else if (onSymbolSelect) onSymbolSelect(chartKey);
              };
              const handleRowTap = () => {
                if (mobileFullscreen) openRowActionSheet(row, chartKey);
                else handleSelect();
              };
              const openCtx = (clientX, clientY) => {
                if (mobileFullscreen) {
                  openRowActionSheet(row, chartKey);
                  return;
                }
                setCtxMenu({ x: clientX, y: clientY, row, chartKey: chartKey });
              };
              const onRowContextMenu = (e) => {
                e.preventDefault();
                openCtx(e.clientX, e.clientY);
              };
              const onRowTouchStart = (e) => {
                const touch = e.touches?.[0];
                if (longPressRef.current) clearTimeout(longPressRef.current);
                longPressRef.current = setTimeout(() => {
                  openCtx(
                    touch?.clientX ?? (typeof window !== "undefined" ? window.innerWidth / 2 : 0),
                    touch?.clientY ?? 140
                  );
                }, 550);
              };
              const clearLongPress = () => {
                if (longPressRef.current) {
                  clearTimeout(longPressRef.current);
                  longPressRef.current = null;
                }
              };
              const isDnDSource = rowDragOk && dragSourceIdx === idx;
              const isDnDDropTarget =
                rowDragOk && idx >= 0 && dragOverIdx === idx && dragSourceIdx !== null && dragSourceIdx !== idx;

              return (
                <div
                  key={row?.id ?? `${displayLabel}-${fIdx}`}
                  className={[
                    rowClass,
                    "mw-watchlist-row",
                    mobileFullscreen ? "mw-watchlist-row--mobile-full" : "",
                    isSelected ? "mw-watchlist-row--selected" : "",
                    isDnDSource ? "mw-dnd-source" : "",
                    isDnDDropTarget ? "mw-dnd-drop-target" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  data-mw-idx={rowDragOk && idx >= 0 ? String(idx) : undefined}
                  onClick={handleRowTap}
                  onDoubleClick={(e) => {
                    e.preventDefault();
                    if (mobileFullscreen) return;
                    handleSelect();
                    if (!viewOnly && onOpenQuickOrder) onOpenQuickOrder(row);
                  }}
                  onContextMenu={onRowContextMenu}
                  onTouchStart={onRowTouchStart}
                  onTouchEnd={clearLongPress}
                  onTouchMove={clearLongPress}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) =>
                    e.key === "Enter" && handleSelect()
                  }
                  title={
                    String(row?.description ?? row?.Description ?? "").trim() || undefined
                  }
                  style={{
                    display: "grid",
                    gridTemplateColumns: watchlistGridCols,
                    boxSizing: "border-box",
                    minWidth: watchlistGridMinOuterPx,
                    width: "100%",
                    columnGap: isExpanded && !mobileFullscreen ? expandedGap : wlColGap,
                    padding: wlPadRow,
                    borderBottom: "none",
                    fontSize: wlFontRow,
                    alignItems: "center",
                    cursor: "pointer",
                    background: rowBg,
                    borderLeft: "3px solid transparent",
                  }}
                >
                  {!mt5CollapsedQuotes && (
                  <div
                    className={[
                      "mw-wl-col",
                      "mw-wl-col--grip",
                      "mw-drag-grip",
                      isDnDSource ? "mw-drag-grip--active" : "",
                      freezeGripSymbol ? "mw-wl-sticky mw-wl-sticky--grip" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    role="separator"
                    aria-orientation="vertical"
                    aria-label={rowDragOk ? "Drag to reorder row" : undefined}
                    onPointerDown={(e) => {
                      if (!rowDragOk || e.button !== 0) return;
                      e.preventDefault();
                      e.stopPropagation();
                      try {
                        e.currentTarget.setPointerCapture(e.pointerId);
                      } catch (_) {
                        /* ignore */
                      }
                      mwDragPtrRef.current = { pointerId: e.pointerId, fromIdx: idx };
                      mwReorderOverRef.current = null;
                      setDragSourceIdx(idx);
                      setDragOverIdx(null);
                    }}
                    onPointerMove={(e) => {
                      if (!mwDragPtrRef.current || e.pointerId !== mwDragPtrRef.current.pointerId) return;
                      scheduleMwOverHitTest(e.clientX, e.clientY);
                    }}
                    onPointerUp={(e) => {
                      if (!mwDragPtrRef.current || e.pointerId !== mwDragPtrRef.current.pointerId) return;
                      const fromIdx = mwDragPtrRef.current.fromIdx;
                      cancelMwOverHitRaf();
                      mwDragPtrRef.current = null;
                      let toIdx = null;
                      const stack = document.elementsFromPoint(e.clientX, e.clientY);
                      for (const el of stack) {
                        if (!(el instanceof Element)) continue;
                        const row = el.closest(".mw-watchlist-row");
                        const raw = row?.getAttribute("data-mw-idx");
                        if (raw != null && raw !== "") {
                          const i = parseInt(raw, 10);
                          if (Number.isFinite(i)) {
                            toIdx = i;
                            break;
                          }
                        }
                      }
                      mwReorderOverRef.current = null;
                      try {
                        e.currentTarget.releasePointerCapture(e.pointerId);
                      } catch (_) {
                        /* ignore */
                      }
                      setDragSourceIdx(null);
                      setDragOverIdx(null);
                      if (toIdx == null || !Number.isFinite(toIdx) || fromIdx === toIdx) return;
                      moveRowOrderRef.current(fromIdx, toIdx);
                    }}
                    onPointerCancel={(e) => {
                      if (!mwDragPtrRef.current || e.pointerId !== mwDragPtrRef.current.pointerId) return;
                      cancelMwOverHitRaf();
                      mwDragPtrRef.current = null;
                      mwReorderOverRef.current = null;
                      try {
                        e.currentTarget.releasePointerCapture(e.pointerId);
                      } catch (_) {
                        /* ignore */
                      }
                      setDragSourceIdx(null);
                      setDragOverIdx(null);
                    }}
                    onLostPointerCapture={() => {
                      cancelMwOverHitRaf();
                      mwDragPtrRef.current = null;
                      mwReorderOverRef.current = null;
                      setDragSourceIdx(null);
                      setDragOverIdx(null);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    title={rowDragOk ? "Drag to reorder" : undefined}
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: mobileFullscreen ? 30 : 28,
                      touchAction: "none",
                      cursor: rowDragOk ? "grab" : "default",
                      color: ui.mutedText,
                      fontSize: mobileFullscreen ? 13 : 14,
                      fontWeight: 700,
                      letterSpacing: "-0.12em",
                      userSelect: "none",
                      borderRadius: 6,
                      ...(freezeGripSymbol ? { background: rowBg } : {}),
                    }}
                  >
                    ⋮⋮
                  </div>
                  )}
                  <div
                    className={[
                      "mw-wl-col",
                      "mw-wl-col--symbol",
                      "mw-mt5-symbol-cell",
                      freezeGripSymbol ? "mw-wl-sticky mw-wl-sticky--sym" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                    style={{
                      color: ui.panelText,
                      fontWeight: 400,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: 1.25,
                      minWidth: 0,
                      textAlign: "left",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 0,
                      WebkitFontSmoothing: "antialiased",
                      ...(freezeGripSymbol
                        ? {
                            background:
                              rowBg ||
                              (isSelected
                                ? darkTheme
                                  ? DARK_UI.rowSelected
                                  : "#f3f8fc"
                                : darkTheme
                                  ? DARK_UI.bg
                                  : ui.rowBg),
                          }
                        : {}),
                    }}
                  >
                    <MwMt5DirIcon dir={mwMt5DirFromRow(row)} />
                    {mobileFullscreen ? (
                      <MobileSymbolLabel fullLabel={fullLabel} />
                    ) : (
                    <span className="mw-mt5-symbol-text" title={fullLabel}>
                      <span
                        className="mw-symbol-label"
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "100%",
                          letterSpacing: 0,
                        }}
                      >
                        {displayLabel}
                      </span>
                      {viewOnly && (
                        <span className="mw-view-only-symbol-tag" title="View-only login">
                          VIEW
                        </span>
                      )}
                    </span>
                    )}
                  </div>
                  {showBidAskCols && (
                    <>
                      <div
                        draggable={false}
                        onDragStart={(e) => e.preventDefault()}
                        className={[
                          "mw-wl-col",
                          "mw-wl-col--price",
                          freezeMobileBidAsk ? "mw-wl-sticky mw-wl-sticky--bid" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        style={{ ...numCellWrap, width: "100%" }}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => onBidAskCellClick(e, row, chartKey)}
                        onDoubleClick={(e) => onBidAskCellDoubleClick(e, row, chartKey)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.stopPropagation();
                            onBidAskCellClick(e, row, chartKey);
                          }
                        }}
                      >
                        <span
                          className="mw-mt5-tick"
                          style={{
                            ...numCellEllipsis,
                            color: colorBid,
                            fontWeight: 400,
                            cursor: viewOnly ? "default" : "pointer",
                          }}
                        >
                          {fmtLive(bid)}
                        </span>
                      </div>
                      <div
                        draggable={false}
                        onDragStart={(e) => e.preventDefault()}
                        className={[
                          "mw-wl-col",
                          "mw-wl-col--price",
                          freezeMobileBidAsk ? "mw-wl-sticky mw-wl-sticky--ask" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        style={{ ...numCellWrap, width: "100%" }}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => onBidAskCellClick(e, row, chartKey)}
                        onDoubleClick={(e) => onBidAskCellDoubleClick(e, row, chartKey)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.stopPropagation();
                            onBidAskCellClick(e, row, chartKey);
                          }
                        }}
                      >
                        <span
                          className="mw-mt5-tick"
                          style={{
                            ...numCellEllipsis,
                            color: colorAsk,
                            fontWeight: 400,
                            cursor: viewOnly ? "default" : "pointer",
                          }}
                        >
                          {fmtLive(ask)}
                        </span>
                      </div>
                    </>
                  )}
                  {mt5CollapsedQuotes ? (
                    mt5MobileQuoteCols ? (
                        <div
                          className="mw-wl-col mw-wl-col--price mw-wl-col--chg"
                          draggable={false}
                          onDragStart={(e) => e.preventDefault()}
                          style={{ ...numCellWrap, width: "100%" }}
                        >
                          <MwMt5DailyChangeCell row={row} />
                        </div>
                    ) : (
                    <div
                      className="mw-wl-col mw-wl-col--price"
                      draggable={false}
                      onDragStart={(e) => e.preventDefault()}
                      style={{ ...numCellWrap, width: "100%" }}
                    >
                      <span
                        className="mw-mt5-tick"
                        style={{
                          ...numCellEllipsis,
                          color: colorLTP,
                          fontWeight: 400,
                        }}
                      >
                        {fmtLive(ltp)}
                      </span>
                    </div>
                    )
                  ) : (
                    <>
                      <div
                        className="mw-wl-col mw-wl-col--price"
                        draggable={false}
                        onDragStart={(e) => e.preventDefault()}
                        style={{ ...numCellWrap, width: "100%" }}
                      >
                        <MwMt5DailyChangeCell row={row} />
                      </div>
                      <div
                        className="mw-wl-col mw-wl-col--price"
                        draggable={false}
                        onDragStart={(e) => e.preventDefault()}
                        style={{ ...numCellWrap, width: "100%" }}
                      >
                        <span style={{ ...numCellEllipsis, color: colorLTP || ui.panelText, fontWeight: 400 }}>
                          {fmtLive(ltp)}
                        </span>
                      </div>
                    </>
                  )}
                  {showOptionalCols
                    ? renderMwOptionalColCells(activeOptionalColDefs, row, ui, fmt, fmtInt)
                    : null}
                  {!mt5CollapsedQuotes && (
                    <div
                      className="mw-wl-col mw-wl-col--remove mw-watchlist-remove-cell"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {!viewOnly && (
                        <MwWatchlistRemoveButton
                          busy={deletingSymbol.has(
                            (row?.displayName || row?.script || row?.symbol || "") ||
                              String(row?.id ?? row?.Id ?? "")
                          )}
                          onClick={(e) => handleRemoveRow(e, row)}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            </div>
            </>
            )}
          </div>
          {viewOnly && (
            <div className="mw-view-only-footer" role="status">
              View only — quotes &amp; chart for viewing; use trading password to place orders.
            </div>
          )}
        </div>
      )}

      {/* SEGMENTS */}
      {view === "segments" && (
        <div
          className={`mw-browse-panel${mt5Ui ? " mw-browse-panel--mt5" : ""}`}
          data-browse-expiry={browseExpiryVersion}
        >
          {viewOnly && (
            <div className="mw-view-only-footer mw-browse-panel__banner" role="status">
              View only — browse symbols; trading requires your main password.
            </div>
          )}
          {searchText.trim().length >= 2 && (
            <div
              className="mw-browse-panel__global-hits"
              style={{
                padding: "10px 12px",
                borderBottom: `1px solid ${ui.border}`,
                background: ui.cardBg,
                maxHeight: 200,
                overflowY: "auto",
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 700, color: ui.panelText, marginBottom: 8 }}>
                Symbols (all segments) · {globalSearchLoading ? "Searching…" : `${globalScriptHits.length} match(es)`}
              </div>
              {!globalSearchLoading &&
                globalScriptHits.length === 0 &&
                segments.length > 0 && (
                  <div style={{ fontSize: 12, color: ui.mutedText }}>No symbol API hits — choose a segment below.</div>
                )}
              {globalScriptHits.map((h, gi) => {
                const name = browseRowBaseName(h.name);
                const added = isInWatchlist(name);
                const busy = addingSymbol.has(name) || deletingSymbol.has(name);
                const desc = browseRowDescription(h);
                return (
                  <div
                    key={`g-${h.segment?.value}-${h.name}-${gi}`}
                    className="mw-browse-panel__row mw-browse-panel__row--symbol"
                  >
                    <div className="mw-browse-panel__symbol-meta">
                      <span className="mw-browse-panel__symbol-name">
                        {globalHitShownLabel(h, browseExpirySuffixRef.current)}
                      </span>
                      {desc || h.segment?.label ? (
                        <span className="mw-browse-panel__symbol-desc">
                          {desc || h.segment?.label}
                        </span>
                      ) : null}
                    </div>
                    {!viewOnly && (
                      <button
                        type="button"
                        className={`mw-browse-panel__add-btn${added ? " mw-browse-panel__add-btn--added" : ""}`}
                        disabled={busy}
                        title={added ? "Click to remove" : "Add with default expiry for segment"}
                        aria-label={added ? "Remove from watchlist" : "Add to watchlist"}
                        onClick={() =>
                          added
                            ? handleRemoveByName(name)
                            : addSymbolForSegment(h.segment, name, { stayOnView: true })
                        }
                      >
                        {addingSymbol.has(name) ? "..." : deletingSymbol.has(name) ? "…" : added ? "✓" : "+"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <div className="mw-browse-panel__scroll">
            {segments.length === 0 && <div style={{ padding: 12 }}>Loading segments...</div>}
            {segments.map((seg) => {
              const selectedCount = segmentSelectedCounts.get(String(seg.value)) || 0;
              const totalCount = getSegmentTotalCount(seg.value);
              const isActive = selectedSegment?.value === seg.value;
              return (
                <div
                  key={seg.value}
                  className={[
                    "mw-browse-panel__row",
                    "mw-browse-panel__row--segment",
                    isActive ? "mw-browse-panel__row--active" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setSelectedSegment(seg);
                      setView("symbols");
                      loadSymbols(seg);
                    }
                  }}
                  onClick={() => {
                    setSelectedSegment(seg);
                    setView("symbols");
                    loadSymbols(seg);
                  }}
                >
                  <span className="mw-browse-panel__segment-label">{seg.label}</span>
                  <span className="mw-browse-panel__segment-count">
                    {totalCount != null ? `${selectedCount}/${totalCount}` : selectedCount > 0 ? String(selectedCount) : ""}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SYMBOLS (e.g. NSE FUT) — scroll inside panel; parent .symbol-panel is overflow:hidden */}
      {view === "symbols" && (
        <div
          className={`mw-browse-panel${mt5Ui ? " mw-browse-panel--mt5" : ""}`}
          data-browse-expiry={browseExpiryVersion}
        >
          {viewOnly && (
            <div className="mw-view-only-footer mw-browse-panel__banner" role="status">
              View only
            </div>
          )}
          <div
            className="mw-browse-panel__header"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && setView("segments")}
            onClick={() => setView("segments")}
          >
            <span className="mw-browse-panel__back">
              <svg className="mw-browse-panel__back-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M14.5 6.5L9 12l5.5 5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="mw-browse-panel__back-label">{selectedSegment?.label}</span>
            </span>
            <span className="mw-browse-panel__header-count">
              {(() => {
                const selectedCount = symbols.reduce(
                  (n, sym) => n + (isInWatchlist(sym) ? 1 : 0),
                  0
                );
                const total = symbols.length || getSegmentTotalCount(selectedSegment?.value) || 0;
                return `${selectedCount} / ${total}`;
              })()}
            </span>
          </div>
          <div className="mw-browse-panel__scroll">
            {symbolsLoading && <div style={{ padding: 12 }}>Loading symbols...</div>}
            {!symbolsLoading && filteredSymbols.length === 0 && (
              <div style={{ padding: 12 }}>No symbols found</div>
            )}
            {!symbolsLoading &&
              filteredSymbols.map((sym, i) => {
                const name = browseRowBaseName(sym);
                const shown = browseRowShownLabel(sym, selectedSegment?.value, browseExpirySuffixRef.current);
                const added = isInWatchlist(sym);
                const busy = addingSymbol.has(name) || deletingSymbol.has(name);
                const desc = browseRowDescription(sym);
                return (
                  <div key={`${name}-${i}`} className="mw-browse-panel__row mw-browse-panel__row--symbol">
                    <div className="mw-browse-panel__symbol-meta">
                      <span className="mw-browse-panel__symbol-name">{shown}</span>
                      {desc ? (
                        <span className="mw-browse-panel__symbol-desc">{desc}</span>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      disabled={busy}
                      title={added ? "Click to remove" : "Add to watchlist"}
                      aria-label={added ? "Remove from watchlist" : "Add to watchlist"}
                      className={`mw-browse-panel__add-btn${added ? " mw-browse-panel__add-btn--added" : ""}`}
                      onClick={() => (added ? handleRemoveByName(name) : handleAdd(sym))}
                    >
                      {addingSymbol.has(name) ? "..." : deletingSymbol.has(name) ? "…" : added ? "✓" : "+"}
                    </button>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {mwColumnsPageOpen && mobileFullscreen && (
        <div className="mw-columns-page" role="dialog" aria-modal="true" aria-label="Watchlist columns">
          <header className="mw-columns-page__header">
            <button
              type="button"
              className="mw-columns-page__back"
              aria-label="Back to watchlist"
              onClick={() => setMwColumnsPageOpen(false)}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M14.5 6.5L9 12l5.5 5.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <h2 className="mw-columns-page__title">Columns</h2>
            <span className="mw-columns-page__spacer" aria-hidden />
          </header>
          <div className="mw-columns-page__scroll">
            <ul className="mw-columns-list" role="listbox" aria-label="Optional watchlist columns">
              {MW_OPTIONAL_WATCH_COLS.map((col) => {
                const enabled = mwOptionalColIds.includes(col.id);
                return (
                  <li key={col.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={enabled}
                      className={`mw-columns-row${enabled ? " mw-columns-row--on" : ""}`}
                      onClick={() => toggleMwOptionalCol(col.id)}
                    >
                      <span className="mw-columns-row__label">{col.label}</span>
                      <span
                        className={`mw-columns-row__tick${enabled ? " mw-columns-row__tick--on" : ""}`}
                        aria-hidden
                      >
                        {enabled ? <MwColCheckMark /> : null}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {clearWatchlistOpen && (
        <div className="mw-clear-watchlist" role="dialog" aria-modal="true" aria-labelledby="mw-clear-watchlist-title">
          <button
            type="button"
            className="mw-clear-watchlist__backdrop"
            aria-label="Cancel"
            disabled={clearingAll}
            onClick={() => setClearWatchlistOpen(false)}
          />
          <div className="mw-clear-watchlist__card">
            <div className="mw-clear-watchlist__icon-wrap" aria-hidden>
              <svg className="mw-clear-watchlist__icon" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 9v4M12 17h.01"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h3 id="mw-clear-watchlist-title" className="mw-clear-watchlist__title">
              Clear watchlist?
            </h3>
            <p className="mw-clear-watchlist__text">
              This will remove{" "}
              <strong>{clearableWatchRows.length}</strong>{" "}
              symbol{clearableWatchRows.length === 1 ? "" : "s"} from your watchlist. You can add
              them again anytime.
            </p>
            <div className="mw-clear-watchlist__actions">
              <button
                type="button"
                className="mw-clear-watchlist__btn mw-clear-watchlist__btn--cancel"
                disabled={clearingAll}
                onClick={() => setClearWatchlistOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="mw-clear-watchlist__btn mw-clear-watchlist__btn--danger"
                disabled={clearingAll}
                onClick={handleClearAllWatchlist}
              >
                {clearingAll ? "Removing…" : "Remove all"}
              </button>
            </div>
          </div>
        </div>
      )}

      {rowActionSheet && (() => {
        const liveRow = rowActionLiveRow ?? rowActionSheet.row;
        const sheetParts =
          rowActionSheet.symParts || splitWatchlistLabel(rowActionSheet.fullLabel);
        const sheetName = sheetParts?.name || rowActionSheet.fullLabel;
        const sheetExpiry = sheetParts?.expiry;
        const rowSheet = (
        <div
          className={`mw-row-action-sheet mw-row-action-sheet--mt5${darkTheme ? " mw-row-action-sheet--dark" : ""}`}
          role="dialog"
          aria-modal="true"
          aria-label={t("symbol")}
        >
          <button
            type="button"
            className="mw-row-action-sheet__backdrop"
            aria-label={t("close")}
            onClick={() => setRowActionSheet(null)}
          />
          <div className="mw-row-action-sheet__panel">
            <div className="mw-row-action-sheet__symbol">{sheetName}</div>
            {sheetExpiry ? (
              <div className="mw-row-action-sheet__expiry">{sheetExpiry}</div>
            ) : null}
            <button
              type="button"
              className="mw-row-action-sheet__item"
              disabled={viewOnly || !onOpenQuickOrder}
              onClick={() => {
                onOpenQuickOrder?.(liveRow);
                setRowActionSheet(null);
              }}
            >
              {t("newOrder")}
            </button>
            <button
              type="button"
              className="mw-row-action-sheet__item"
              onClick={() => {
                if (onSymbolSelectWithRow) {
                  onSymbolSelectWithRow(rowActionSheet.chartKey, liveRow);
                } else if (onSymbolSelect) {
                  onSymbolSelect(rowActionSheet.chartKey);
                }
                setRowActionSheet(null);
              }}
            >
              {t("chart")}
            </button>
            <button
              type="button"
              className="mw-row-action-sheet__item"
              onClick={() => {
                setSpecRow(liveRow);
                setRowActionSheet(null);
              }}
            >
              {t("specification")}
            </button>
            {!viewOnly && (
              <button
                type="button"
                className="mw-row-action-sheet__item"
                onClick={(e) => {
                  handleRemoveRow(e, liveRow);
                  setRowActionSheet(null);
                }}
              >
                {t("hide")}
              </button>
            )}
          </div>
        </div>
        );
        return mobileFullscreen && typeof document !== "undefined"
          ? createPortal(rowSheet, document.body)
          : rowSheet;
      })()}

      {ctxMenu && (
        <div
          className="mw-ctx-menu"
          style={{
            position: "fixed",
            left: Math.max(
              8,
              Math.min(
                ctxMenu.x,
                (typeof window !== "undefined" ? window.innerWidth : 360) -
                  Math.min(190, (typeof window !== "undefined" ? window.innerWidth : 360) - 16)
              )
            ),
            top: Math.max(
              8,
              Math.min(
                ctxMenu.y,
                (typeof window !== "undefined" ? window.innerHeight : 400) - 220
              )
            ),
            zIndex: 4000,
            background: "#fffef9",
            border: "1px solid #c4bbb0",
            borderRadius: 8,
            boxShadow: "0 8px 28px rgba(0,0,0,.18)",
            minWidth: 172,
            padding: 6,
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            style={ctxBtnStyle}
            disabled={viewOnly || !onOpenQuickOrder}
            onClick={() => {
              onOpenQuickOrder?.(ctxMenu.row);
              setCtxMenu(null);
            }}
          >
            {t("newOrder")}
          </button>
          <button
            type="button"
            style={ctxBtnStyle}
            onClick={() => {
              if (onSymbolSelectWithRow) onSymbolSelectWithRow(ctxMenu.chartKey, ctxMenu.row);
              else if (onSymbolSelect) onSymbolSelect(ctxMenu.chartKey);
              setCtxMenu(null);
            }}
          >
            {t("chart")}
          </button>
          <button
            type="button"
            style={ctxBtnStyle}
            onClick={() => {
              setSpecRow(ctxMenu.row);
              setCtxMenu(null);
            }}
          >
            {t("specification")}
          </button>
          {!viewOnly && (
            <button
              type="button"
              style={{
                ...ctxBtnStyle,
                color: "#9a3412",
                fontWeight: 700,
                borderTop: "1px solid #e8e0d4",
                marginTop: 2,
                paddingTop: 8,
              }}
              onClick={(e) => {
                handleRemoveRow(e, ctxMenu.row);
                setCtxMenu(null);
              }}
            >
              {t("removeFromWatchlist")}
            </button>
          )}
        </div>
      )}

      {specRow &&
        (() => {
          const specSymbol = splitWatchlistLabel(
            specRow?.symbol || specRow?.script || specRow?.displayName || "—"
          ).name;
          const specSheet = (
            <div
              className={`mw-spec-sheet${mobileFullscreen ? " mw-spec-sheet--mobile" : ""}${
                darkTheme ? " mw-spec-sheet--dark" : ""
              }`}
              role="dialog"
              aria-modal="true"
              aria-label={t("specification")}
            >
              <button
                type="button"
                className="mw-spec-sheet__backdrop"
                aria-label={t("close")}
                onClick={() => setSpecRow(null)}
              />
              <div className="mw-spec-sheet__panel">
                <div className="mw-spec-sheet__handle" aria-hidden />
                <div className="mw-spec-sheet__head">
                  {mobileFullscreen ? (
                    <div className="mw-spec-sheet__title">{specSymbol}</div>
                  ) : (
                    <div>
                      <div className="mw-spec-sheet__title">{t("specification")}</div>
                      <div className="mw-spec-sheet__subtitle">{specSymbol}</div>
                    </div>
                  )}
                  <button
                    type="button"
                    className="mw-spec-sheet__close"
                    aria-label={t("close")}
                    onClick={() => setSpecRow(null)}
                  >
                    ✕
                  </button>
                </div>
                <div className="mw-spec-sheet__body" ref={specBodyRef}>
                  {specSections.map((section, sIdx) => (
                    <div
                      key={section.title}
                      className={`mw-spec-sheet__section${
                        mobileFullscreen && sIdx === 0 ? " mw-spec-sheet__section--tiles" : ""
                      }`}
                    >
                      <div className="mw-spec-sheet__section-title">{section.title}</div>
                      <div className="mw-spec-sheet__grid">
                        {section.rows.map((item) => (
                          <div key={`${section.title}-${item.label}`} className="mw-spec-sheet__row">
                            <span className="mw-spec-sheet__label">{item.label}</span>
                            <span className="mw-spec-sheet__value">{item.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
          return mobileFullscreen && typeof document !== "undefined"
            ? createPortal(specSheet, document.body)
            : specSheet;
        })()}
      {mwColTip &&
        createPortal(
          <div
            className="fx-mt5-tip"
            style={{ left: mwColTip.x, top: mwColTip.y }}
            role="tooltip"
          >
            {mwColTip.text}
          </div>,
          document.body
        )}
    </div>
  );
});

const ctxBtnStyle = {
  textAlign: "left",
  padding: "10px 12px",
  border: "1px solid #d6d0c7",
  borderRadius: 6,
  background: "#f3f1ed",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  color: "#2a2218",
};

export default MarketWatch;